import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import * as XLSX from "xlsx";

/* ═══════════════════════════════════════════════════════════
   STATIC DATA
   ═══════════════════════════════════════════════════════════ */

// Full granular outcome-type taxonomy — replaces the old 12-item list.
// Compiled as the union of every Review Type's outcome set below, in the
// order each first appears, plus "Other" kept as the free-text escape hatch.
const OUTCOME_TYPES = [
    // Clinical Review
    "Treatment effectiveness",
    "Safety/tolerability",
    "Clinical response",
    "Disease progression",
    "Treatment discontinuation",
    // Economic Review
    "Direct costs",
    "Indirect costs",
    "Healthcare resource utilization",
    "Cost-effectiveness results",
    "Budget impact",
    // Humanistic Review
    "Health-related quality of life",
    "Patient-reported outcomes",
    "Symptom burden",
    "Functional status",
    "Treatment satisfaction",
    // Utility Review
    "Health state utility values",
    "Utility decrements",
    "Preference weights",
    "Utility elicitation results",
    "Mapped utility values",
    // Treatment Guidelines/Patterns
    "Treatment sequencing",
    "Standard of care",
    "Prescribing patterns",
    "Treatment switching",
    "Guideline recommendations",
    // Adherence/persistence
    "Medication adherence",
    "Treatment persistence",
    "Compliance rates",
    "Treatment gaps/interruption",
    // Epidemiology review
    "Incidence",
    "Prevalence",
    "Mortality",
    "Recurrence/progression rates",
    "Disease burden",
    // Prognostic review
    "Prognostic factors",
    "Risk stratification",
    "Predictive markers",
    "Association with outcomes",
    "Prognostic model performance",
    // Surrogate endpoints review
    "Biomarkers",
    "Laboratory measures",
    "Imaging measures",
    "Early/intermediate clinical outcomes",
    "Correlation with final clinical outcomes",
    "Other",
];

/* Review Type — selected before the rest of the form, single-select (or a
   validated pair). Picking one deterministically auto-checks its matching
   Outcome Types below (see REVIEW_TYPE_OUTCOME_MAP) and is also sent to
   /auto-fill-form as context alongside the research question, same pattern
   as the existing "framework" field. */
const REVIEW_TYPES = [
    { key: "CLINICAL_REVIEW", label: "Clinical Review" },
    { key: "ECONOMIC_REVIEW", label: "Economic Review" },
    { key: "HUMANISTIC_REVIEW", label: "Humanistic Review" },
    { key: "UTILITY_REVIEW", label: "Utility Review" },
    { key: "TREATMENT_GUIDELINES_REVIEW", label: "Treatment Guidelines/Patterns" },
    { key: "ADHERENCE_PERSISTENCE_REVIEW", label: "Adherence/Persistence" },
    { key: "EPIDEMIOLOGY_REVIEW", label: "Epidemiology Review" },
    { key: "PROGNOSTIC_REVIEW", label: "Prognostic Review" },
    { key: "SURROGATE_ENDPOINTS_REVIEW", label: "Surrogate Endpoints Review" },
];

// Which OUTCOME_TYPES labels are valid for each Review Type. "Other" is
// neutral and never counted as a mismatch, regardless of Review Type.
const REVIEW_TYPE_OUTCOME_MAP = {
    CLINICAL_REVIEW: ["Treatment effectiveness", "Safety/tolerability", "Clinical response", "Disease progression", "Treatment discontinuation"],
    ECONOMIC_REVIEW: ["Direct costs", "Indirect costs", "Healthcare resource utilization", "Cost-effectiveness results", "Budget impact"],
    HUMANISTIC_REVIEW: ["Health-related quality of life", "Patient-reported outcomes", "Symptom burden", "Functional status", "Treatment satisfaction"],
    UTILITY_REVIEW: ["Health state utility values", "Utility decrements", "Preference weights", "Utility elicitation results", "Mapped utility values"],
    TREATMENT_GUIDELINES_REVIEW: ["Treatment sequencing", "Standard of care", "Prescribing patterns", "Treatment switching", "Guideline recommendations"],
    ADHERENCE_PERSISTENCE_REVIEW: ["Medication adherence", "Treatment persistence", "Compliance rates", "Treatment switching", "Treatment gaps/interruption"],
    EPIDEMIOLOGY_REVIEW: ["Incidence", "Prevalence", "Mortality", "Recurrence/progression rates", "Disease burden"],
    PROGNOSTIC_REVIEW: ["Prognostic factors", "Risk stratification", "Predictive markers", "Association with outcomes", "Prognostic model performance"],
    SURROGATE_ENDPOINTS_REVIEW: ["Biomarkers", "Laboratory measures", "Imaging measures", "Early/intermediate clinical outcomes", "Correlation with final clinical outcomes"],
};

/* Which STUDY_DESIGN_OPTIONS keys are auto-checked for each Review Type.
   Convenience only — unlike REVIEW_TYPE_OUTCOME_MAP, this is NOT enforced by
   getReviewTypeGuardMessage; the user can freely add/remove Study Design
   filters afterward without being blocked.

   Reasoned from SLR methodology, not transcribed verbatim from the
   reference table — several of the table's per-Review-Type "Study Design"
   entries were actually outcome concepts or a publication type in disguise
   (e.g. "HRQoL/PRO burden", "Utility data", "Adherence and persistence",
   "Incidence/prevalence/mortality", "Treatment guidelines"), not a genuine
   study design. A Study Design filter should only ever describe the
   METHODOLOGY that generated the evidence — RCT vs non-randomized vs
   observational/real-world vs economic-model — never the topic a review is
   about; the topic already has its home in Outcome Types (and, for
   guidelines specifically, Publication Type). Reusing an outcome concept as
   a second, differently-labelled "design" checkbox would just duplicate it
   and confuse the two purposes.
     - Clinical Review: RCT + Observational/RWE — the two validated,
       non-overlapping designs. Clinical Trial and Non-Randomized CT stay
       selectable (see VISIBLE below) for a narrower deliberate choice, but
       aren't auto-checked alongside RCT — Clinical Trial's own coverage
       already includes RCT, so checking both by default would have the LLM
       draft near-duplicate content for no added recall.
     - Economic Review: Economic Evaluations only — the CADTH-validated
       design. Cost/resource-use and cost-effectiveness are RESULTS reported
       inside an economic evaluation, not a separate design to search for.
     - Humanistic Review & Utility Review: HRQoL/PRO and utility values are
       collected within RCTs, observational studies, and (for utility)
       economic evaluations — not their own design. Both use the designs
       that actually generate this data.
     - Treatment Guidelines/Patterns: "guidelines" is a publication type
       (see REVIEW_TYPE_PUBLICATION_TYPE_MAP, unchanged); "patterns" studies
       are near-universally retrospective/real-world by methodology.
     - Adherence/Persistence, Epidemiology, Prognostic: each of these is
       studied almost exclusively via observational/real-world designs
       (claims/EHR data, registries, cohort studies) — genuinely their
       design, not a renamed outcome.
     - Surrogate Endpoints: RCT + Observational/RWE, unchanged — surrogate/
       biomarker endpoints are legitimately reported in both trial types,
       this one was already a real design combination in the reference
       table, not a mislabelled outcome. */
const REVIEW_TYPE_STUDY_DESIGN_MAP = {
    CLINICAL_REVIEW: ["RCT", "OBSERVATIONAL_RWE"],
    ECONOMIC_REVIEW: ["ECONOMIC"],
    HUMANISTIC_REVIEW: ["RCT", "OBSERVATIONAL_RWE"],
    UTILITY_REVIEW: ["RCT", "OBSERVATIONAL_RWE", "ECONOMIC"],
    TREATMENT_GUIDELINES_REVIEW: ["OBSERVATIONAL_RWE"],
    ADHERENCE_PERSISTENCE_REVIEW: ["OBSERVATIONAL_RWE"],
    EPIDEMIOLOGY_REVIEW: ["OBSERVATIONAL_RWE"],
    PROGNOSTIC_REVIEW: ["OBSERVATIONAL_RWE"],
    SURROGATE_ENDPOINTS_REVIEW: ["RCT", "OBSERVATIONAL_RWE"],
};

/* Which Study Design checkboxes are shown at all for each Review Type — a
   superset of REVIEW_TYPE_STUDY_DESIGN_MAP above. Clinical Review is the
   one case with genuinely narrower alternative designs worth surfacing
   (Clinical Trial as a broader single-arm-inclusive alternative to RCT,
   Non-Randomized CT for a deliberately non-randomized focus) — every other
   Review Type's visible set is identical to its auto-check set, since there
   is no methodologically distinct narrower/broader design variant to offer
   for those. */
const REVIEW_TYPE_STUDY_DESIGN_VISIBLE = {
    CLINICAL_REVIEW: ["RCT", "CLINICAL_TRIAL", "NON_RANDOMIZED_CT", "OBSERVATIONAL_RWE"],
    ECONOMIC_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.ECONOMIC_REVIEW,
    HUMANISTIC_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.HUMANISTIC_REVIEW,
    UTILITY_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.UTILITY_REVIEW,
    TREATMENT_GUIDELINES_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.TREATMENT_GUIDELINES_REVIEW,
    ADHERENCE_PERSISTENCE_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.ADHERENCE_PERSISTENCE_REVIEW,
    EPIDEMIOLOGY_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.EPIDEMIOLOGY_REVIEW,
    PROGNOSTIC_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.PROGNOSTIC_REVIEW,
    SURROGATE_ENDPOINTS_REVIEW: REVIEW_TYPE_STUDY_DESIGN_MAP.SURROGATE_ENDPOINTS_REVIEW,
};

/* Treatment Guidelines is a publication type, not a study design — guideline
   documents (NCCN/ESMO/ASCO etc.) don't have a population/methodology to
   classify as RCT vs observational the way primary studies do. No other
   Review Type maps to a Publication Type. */
const REVIEW_TYPE_PUBLICATION_TYPE_MAP = {
    CLINICAL_REVIEW: [],
    ECONOMIC_REVIEW: [],
    HUMANISTIC_REVIEW: [],
    UTILITY_REVIEW: [],
    TREATMENT_GUIDELINES_REVIEW: ["GUIDELINES"],
    ADHERENCE_PERSISTENCE_REVIEW: [],
    EPIDEMIOLOGY_REVIEW: [],
    PROGNOSTIC_REVIEW: [],
    SURROGATE_ENDPOINTS_REVIEW: [],
};

/* Multi-select, but only along the combinations that make SLR-methodological
   sense — not a free-for-all. Adjacency list: a key here is only allowed to
   be co-selected with the keys listed. Max group size is 2 (no triples are
   validated combinations).
     - Clinical Review + Treatment Guidelines/Patterns: same evidence base,
       guidelines/patterns largely summarise the same clinical efficacy/
       safety literature.
     - Humanistic Review + Utility Review: utility evidence is a specialised
       subset of HRQoL evidence — same MeSH/instruments/vocabulary.
     - Economic Review + Utility Review: situational — utility values feed
       QALY calculations in a cost-utility model.
   The four new Review Types (Adherence/Persistence, Epidemiology,
   Prognostic, Surrogate Endpoints) are deliberately left standalone here —
   no combination rule was specified for them, so each is single-select-only
   for now rather than guessing a pairing. Say the word if you want any of
   them combinable (e.g. Clinical + Adherence/Persistence would be a
   plausible pairing) and I'll wire it in. */
const REVIEW_TYPE_COMPATIBILITY = {
    CLINICAL_REVIEW: ["TREATMENT_GUIDELINES_REVIEW"],
    TREATMENT_GUIDELINES_REVIEW: ["CLINICAL_REVIEW"],
    HUMANISTIC_REVIEW: ["UTILITY_REVIEW"],
    UTILITY_REVIEW: ["HUMANISTIC_REVIEW", "ECONOMIC_REVIEW"],
    ECONOMIC_REVIEW: ["UTILITY_REVIEW"],
    ADHERENCE_PERSISTENCE_REVIEW: [],
    EPIDEMIOLOGY_REVIEW: [],
    PROGNOSTIC_REVIEW: [],
    SURROGATE_ENDPOINTS_REVIEW: [],
};

/* ── Filter options: {key, label} pairs matching backend PUBMED_FILTER_SYNTAX exactly ── */

// study_design keys: CLINICAL_TRIAL | RCT | RCT_PRECISION | NON_RANDOMIZED_CT | OBSERVATIONAL_RWE | PRAGMATIC_TRIALS | ECONOMIC
//
// Kept to genuine METHODOLOGY only — how the evidence was generated (RCT vs
// observational vs economic-model), not what topic/outcome a review cares
// about. An earlier pass added options like "HRQoL/PRO Burden", "Utility",
// "Treatment Patterns", "Adherence and Persistence", "Incidence/Prevalence/
// Mortality", "Risk Factors", "Prognostic/Effect Modifiers" — these are
// outcome concepts, not designs, and duplicated what OUTCOME_TYPES already
// covers; "Treatment Guidelines" duplicated Publication Type's GUIDELINES;
// "Cost and Resource Use" duplicated the Economic Outcomes/Healthcare
// resource utilization outcome. Reverted — a Study Design filter should
// never need a second, outcome-flavoured entry for something the Outcome
// Types list already names.
const STUDY_DESIGN_OPTIONS = [
    { key: "CLINICAL_TRIAL", label: "Clinical Trial" },
    { key: "RCT", label: "Randomized Controlled Trial" },
    { key: "RCT_PRECISION", label: "RCT (Precision-Maximising)" },
    { key: "NON_RANDOMIZED_CT", label: "Non-Randomized Controlled Clinical Trials" },
    { key: "OBSERVATIONAL_RWE", label: "Observational / Real-World Evidence" },
    { key: "PRAGMATIC_TRIALS", label: "Pragmatic Clinical Trials" },
    { key: "ECONOMIC", label: "Economic Evaluations" },
];
const STUDY_DESIGN_FILTERS = STUDY_DESIGN_OPTIONS.map((o) => o.label);

/* Deterministic, non-LLM Study Design / Economic filter clauses — validated published
   filters (Cochrane HSSS 2023, SIGN/Avau 2021, CADTH/Kaunelis 2011). Keep this in sync
   with STUDY_DESIGN_FILTER_QUERIES in search_form_service.py. Options with no entry here
   (e.g. Clinical Trial, Pragmatic Trials) have no pre-built filter — the AI drafts those. */
const STUDY_DESIGN_QUERY_MAP = {
    RCT: {
        include: '("randomized controlled trial"[PT] OR "controlled clinical trial"[Publication Type] OR "randomized"[Title/Abstract] OR "placebo"[Title/Abstract] OR "drug therapy"[MeSH Subheading] OR "randomly"[Title/Abstract] OR "trial"[Title/Abstract] OR "groups"[Title/Abstract])',
        exclude: null,
    },
    RCT_PRECISION: {
        include: '(("randomized controlled trial"[pt] OR "controlled clinical trial"[pt] OR "randomized"[tiab] OR "placebo"[tiab]) OR ("clinical trials as topic"[mesh:noexp]) OR (randomly[tiab] OR trial[ti]))',
        exclude: null,
    },
    OBSERVATIONAL_RWE: {
        include: '("Epidemiologic Studies"[MeSH] OR "case control"[TIAB] OR "case-control"[TIAB] OR ((case[TIAB] OR cases[TIAB]) AND (control[TIAB] OR controls[TIAB])) OR "cohort study"[TIAB] OR "cohort analysis"[TIAB] OR "follow up study"[TIAB] OR "follow-up study"[TIAB] OR "observational study"[TIAB] OR longitudinal[TIAB] OR retrospective[TIAB] OR "cross sectional"[TIAB] OR questionnaire[TIAB] OR questionnaires[TIAB] OR survey[TIAB])',
        exclude: '("case reports"[pt] OR "case report"[ti] OR "case series"[ti])',
    },
    ECONOMIC: {
        include: '(Economics[majr:noexp] OR "costs and cost analysis"[majr] OR (economic[tiab] AND model*[tiab]) OR cost minimi*[tiab] OR cost-utilit*[tiab] OR health utilit*[tiab] OR economic evaluation*[tiab] OR economic review*[tiab] OR cost outcome[tiab] OR cost analys*[tiab] OR economic analys*[tiab] OR (budget*[tiab] AND impact analys*[tiab]) OR cost-effective*[ti] OR pharmacoeconomic*[ti] OR pharmaco-economic*[ti] OR cost-benefit[ti] OR costs[ti] OR cost-effective*[ot] OR pharmacoeconomic*[ot] OR pharmaco-economic*[ot] OR cost-benefit[ot] OR costs[ot] OR life year[tiab] OR life years[tiab] OR qaly*[tiab] OR cost-benefit analys*[tiab] OR cost-effectiveness analys*[tiab] OR ((cost[ti] OR economic*[ti] OR cost[ot] OR economic*[ot]) AND (costs[tiab] OR cost-effectiveness[tiab] OR markov[tiab])))',
        exclude: null,
    },
};

/* Builds the deterministic Study Design query for the given option keys.
   Returns "" when none of the selected keys have a validated filter defined —
   in that case the AI drafts the Study Design step as before. */
const buildStudyDesignQuery = (keys) => {
    const includes = [];
    let needsExclude = false;
    (keys || []).forEach((key) => {
        const entry = STUDY_DESIGN_QUERY_MAP[key];
        if (!entry) return;
        includes.push(entry.include);
        if (entry.exclude) needsExclude = true;
    });
    if (!includes.length) return "";
    let combined = includes.length === 1 ? includes[0] : `(${includes.join(" OR ")})`;
    if (needsExclude) combined = `${combined} NOT ${STUDY_DESIGN_QUERY_MAP.OBSERVATIONAL_RWE.exclude}`;
    return combined;
};

/* Embase.com — separate map, separate builder from PubMed's above. Different
   platform, different syntax (/exp, /de, :ti,ab,kw, NEAR/n, NEXT/n vs PubMed's
   [MeSH]/[tiab]/[pt]). Keep this in sync with EMBASECOM_STUDY_DESIGN_FILTER_QUERIES
   in search_form_service.py. Only RCT / OBSERVATIONAL_RWE / ECONOMIC have a
   validated Embase.com filter on file — any other selected key (CLINICAL_TRIAL,
   RCT_PRECISION, NON_RANDOMIZED_CT, PRAGMATIC_TRIALS) has no entry here, so the
   AI drafts that piece instead, same fallback behavior as the PubMed map. */
const EMBASECOM_STUDY_DESIGN_QUERY_MAP = {
    RCT: {
        include: "('randomized controlled trial'/exp OR 'controlled clinical trial'/de OR 'randomization'/de OR 'single blind procedure'/de OR 'double blind procedure'/de OR 'crossover procedure'/de OR 'placebo'/de OR 'intermethod comparison'/de OR 'human experiment'/de OR 'clinical trial'/de OR 'phase 3 clinical trial'/de OR 'phase 4 clinical trial'/de OR 'multicenter study'/de OR random*:ti,ab,tt OR placebo*:ti,ab,tt OR rct:ti,ab,tt OR 'random allocation':ti,ab,tt OR 'randomly allocated':ti,ab,tt OR 'allocated randomly':ti,ab,tt OR (allocated NEAR/2 random*):ti,ab,tt OR (random* NEAR/2 (assign* OR allocat*)):ti,ab,tt OR ((singl* OR doubl* OR trebl* OR tripl*) NEAR/1 (blind* OR mask*)):ti,ab,tt OR (open NEXT/1 label):ti,ab,tt OR (parallel NEXT/1 group*):ti,ab,tt OR crossover:ti,ab,tt OR 'cross over':ti,ab,tt OR ((assign* OR match OR matched OR allocation) NEAR/6 (alternate OR group OR groups OR intervention OR interventions OR patient OR patients OR subject OR subjects OR participant OR participants)):ti,ab,tt OR assigned:ti,ab,tt OR allocated:ti,ab,tt OR (controlled NEAR/8 (study OR design OR trial)):ti,ab,tt OR volunteer:ti,ab,tt OR volunteers:ti,ab,tt OR trial:ti,tt OR compare:ti,tt OR compared:ti,tt OR comparison:ti,tt OR ((evaluated:ab OR evaluate:ab OR evaluating:ab OR assessed:ab OR assess:ab) AND (compare:ab OR compared:ab OR comparing:ab OR comparison:ab)))",
        exclude: null,
    },
    OBSERVATIONAL_RWE: {
        include: "('clinical study'/de OR 'observational study'/exp OR 'cohort analysis'/exp OR 'longitudinal study'/exp OR 'retrospective study'/exp OR 'prospective study'/exp OR 'case control study'/exp OR 'cross-sectional study'/exp OR 'family study'/de OR 'comparative effectiveness'/exp OR 'register'/exp OR 'pragmatic trial'/de OR cohort*:ti,ab,kw OR (cohort NEXT/1 (stud* OR analy*)):ti,ab,kw OR (('follow up' OR followup) NEXT/1 (study OR studies)):ti,ab,kw OR ((observational OR epidemiologic* OR 'cross sectional' OR 'case control' OR longitudinal OR retrospective OR prospective OR registry OR register OR database) NEXT/1 (study OR studies OR analys*)):ti,ab,kw OR retrospective:ti,ab,kw OR prospective:ti,ab,kw OR registry:ti,ab,kw OR registries:ti,ab,kw OR register:ti,ab,kw OR ('real' NEXT/1 world):ti,ab,kw OR 'real-world':ti,ab,kw OR 'routinely collected':ti,ab,kw OR (healthcare NEXT/1 record*):ti,ab,kw OR ('health care' NEXT/1 record*):ti,ab,kw OR (electronic NEXT/1 ('health record*' OR 'medical record*')):ti,ab,kw OR (claims NEXT/1 (data OR database*)):ti,ab,kw OR (administrative NEXT/1 (data OR database*)):ti,ab,kw OR ('database' NEAR/2 (study OR studies OR analys*)):ti,ab,kw OR 'chart review':ti,ab,kw OR 'medical record review':ti,ab,kw OR 'secondary data analysis':ti,ab,kw OR 'pragmatic trial':ti,ab,kw OR 'pragmatic clinical trial':ti,ab,kw OR 'real-world clinical trial':ti,ab,kw OR 'real world evidence':ti,ab,kw OR 'real world data':ti,ab,kw OR nonrandom*:ti,ab,kw OR 'non random*':ti,ab,kw OR 'non-randomised':ti,ab,kw OR 'non-randomized':ti,ab,kw OR ((non OR not) NEAR/3 (randomised OR randomized)):ti,ab,kw OR nrct:ti,ab,kw OR 'single arm':ti,ab,kw OR 'single-arm':ti,ab,kw OR 'single agent':ti,ab,kw OR 'single-agent':ti,ab,kw OR 'open label extension':ti,ab,kw OR 'expanded access':ti,ab,kw OR 'compassionate use':ti,ab,kw)",
        exclude: "('case report'/de OR 'case report':ti OR 'case reports':ti OR 'case series':ti OR 'case study':ti OR 'case studies':ti)",
    },
    ECONOMIC: {
        include: "('health economics'/exp OR 'economic aspect'/exp OR 'economic evaluation'/exp OR 'cost'/exp OR 'health care cost'/exp OR 'drug cost'/exp OR 'hospital cost'/exp OR 'cost benefit analysis'/exp OR 'cost effectiveness analysis'/exp OR 'cost minimization analysis'/exp OR 'cost utility analysis'/exp OR 'cost of illness'/exp OR 'cost control'/exp OR 'socioeconomics'/exp OR 'pharmacoeconomics'/exp OR 'health care financing'/exp OR 'financial management'/exp OR 'hospital finance'/exp OR 'budget'/exp OR 'fee'/exp OR 'quality adjusted life year'/exp OR 'statistical model'/de OR 'monte carlo method'/de OR 'decision tree'/de OR 'decision theory'/de OR 'absenteeism'/de OR 'presenteeism'/de OR 'productivity'/de OR 'economic evaluation'/syn OR 'economic model'/syn OR 'pharmacoeconomics'/syn OR economic*:ti,ab,kw OR pharmacoeconomic*:ti,ab,kw OR 'pharmaco economic*':ti,ab,kw OR cost:ti,kw OR costs:ti,kw OR costly:ti,kw OR costing:ti,kw OR price*:ti,ab,kw OR pricing:ti,ab,kw OR expenditure*:ti,ab,kw OR fiscal:ti,ab,kw OR funding:ti,kw OR financial:ti,kw OR finance:ti,kw OR (cost* NEAR/2 (effective* OR efficien* OR utilit* OR minimi* OR consequen* OR benefit* OR unit* OR estimate* OR variable* OR analys* OR outcome* OR saving*)):ti,ab,kw OR ((direct OR indirect OR societal OR incremental OR total OR annual) NEAR/2 cost*):ti,ab,kw OR 'unit cost*':ti,ab,kw OR 'low cost':ti,ab,kw OR 'high cost':ti,ab,kw OR 'cost of illness':ti,ab,kw OR 'societal cost*':ti,ab,kw OR 'social care cost*':ti,ab,kw OR ((healthcare OR 'health care') NEXT/1 cost*):ti,ab,kw OR ((healthcare OR 'health care' OR resource OR resources) NEXT/1 (utilisation OR utilization OR use)):ti,ab,kw OR 'value for money':ti,ab,kw OR (value NEAR/2 (money OR monetary)):ti,ab,kw OR 'quality adjusted life':ti,ab,kw OR qaly*:ti,ab,kw OR qald*:ti,ab,kw OR qale*:ti,ab,kw OR 'disability adjusted life':ti,ab,kw OR daly*:ti,ab,kw OR 'life year gained':ti,ab,kw OR 'life years gained':ti,ab,kw OR 'willingness to pay':ti,ab,kw OR 'time trade off':ti,ab,kw OR 'time tradeoff':ti,ab,kw OR 'standard gamble':ti,ab,kw OR 'net monetary benefit':ti,ab,kw OR 'net health benefit':ti,ab,kw OR 'incremental cost effectiveness ratio':ti,ab,kw OR 'incremental cost-effectiveness ratio':ti,ab,kw OR icer:ti,ab,kw OR 'budget impact':ti,ab,kw OR 'budget impact analysis':ti,ab,kw OR 'budget impact model':ti,ab,kw OR ((markov OR 'monte carlo' OR 'discrete event' OR 'partitioned survival' OR 'decision analytic' OR 'decision-analytic' OR 'health economic') NEAR/3 (method* OR analys* OR model* OR simulation* OR assessment* OR chain* OR stud*)):ti,ab,kw OR markov:ti,ab,kw OR 'monte carlo':ti,ab,kw OR 'discrete event simulation':ti,ab,kw OR 'partitioned survival':ti,ab,kw OR (decision NEAR/2 (theory OR tree OR trees OR analy* OR model*)):ti,ab,kw OR ((cost OR economic) NEAR/3 (method* OR analys* OR model* OR simulation* OR assessment*)):ti,ab,kw OR 'cost effectiveness':ti,ab,kw OR 'cost-effectiveness':ti,ab,kw OR 'cost benefit':ti,ab,kw OR 'cost-benefit':ti,ab,kw OR 'cost utility':ti,ab,kw OR 'cost-utility':ti,ab,kw OR 'cost minimisation':ti,ab,kw OR 'cost minimization':ti,ab,kw OR 'cost consequence':ti,ab,kw OR 'cost-consequence':ti,ab,kw OR cea:ti,ab,kw OR cua:ti,ab,kw OR cba:ti,ab,kw OR cma:ti,ab,kw OR cca:ti,ab,kw OR bia:ti,ab,kw OR 'work productivity':ti,ab,kw OR 'lost productivity':ti,ab,kw OR 'productivity loss':ti,ab,kw)",
        exclude: null,
    },
};

const buildEmbaseComStudyDesignQuery = (keys) => {
    const includes = [];
    let needsExclude = false;
    (keys || []).forEach((key) => {
        const entry = EMBASECOM_STUDY_DESIGN_QUERY_MAP[key];
        if (!entry) return;
        includes.push(entry.include);
        if (entry.exclude) needsExclude = true;
    });
    if (!includes.length) return "";
    let combined = includes.length === 1 ? includes[0] : `(${includes.join(" OR ")})`;
    if (needsExclude) combined = `${combined} NOT ${EMBASECOM_STUDY_DESIGN_QUERY_MAP.OBSERVATIONAL_RWE.exclude}`;
    return combined;
};

/* Embase (Ovid) — a third, separate map/builder again. Different syntax from
   both PubMed and Embase.com: .ti,ab. field codes (trailing period), exp X/
   and X/ for subject headings (no surrounding quotes), $ / $N truncation,
   adjN proximity, lowercase and/or/not. Keep this in sync with
   OVID_STUDY_DESIGN_FILTER_QUERIES in search_form_service.py. Only RCT /
   OBSERVATIONAL_RWE / ECONOMIC have a validated Ovid filter on file — same
   three as Embase.com; anything else falls through to the AI draft path.
   Delimited with single quotes in JS (not double) because the Ovid filter
   text itself contains literal double-quoted phrases ("cross section$" etc.)
   that would otherwise need escaping. */
const OVID_STUDY_DESIGN_QUERY_MAP = {
    RCT: {
        include: '((exp randomized controlled trial/ or controlled clinical trial/ or random$.ti,ab. or randomization/ or intermethod comparison/ or placebo.ti,ab. or (compare or compared or comparison).ti. or ((evaluated or evaluate or evaluating or assessed or assess) and (compare or compared or comparing or comparison)).ab. or (open adj label).ti,ab. or ((double or single or doubly or singly) adj (blind or blinded or blindly)).ti,ab. or double blind procedure/ or parallel group$1.ti,ab. or (crossover or cross over).ti,ab. or ((assign$ or match or matched or allocation) adj5 (alternate or group$1 or intervention$1 or patient$1 or subject$1 or participant$1)).ti,ab. or (assigned or allocated).ti,ab. or (controlled adj7 (study or design or trial)).ti,ab. or (volunteer or volunteers).ti,ab. or human experiment/ or trial.ti.) not (((random$ adj sampl$ adj7 ("cross section$" or questionnaire$1 or survey$ or database$1)).ti,ab. not (comparative study/ or controlled study/ or randomi?ed controlled.ti,ab. or randomly assigned.ti,ab.)) or (cross-sectional study/ not (exp randomized controlled trial/ or controlled clinical trial/ or controlled study/ or randomi?ed controlled.ti,ab. or control group$1.ti,ab.)) or (((case adj control$) and random$) not randomi?ed controlled).ti,ab. or (systematic review.ti,ab. not (trial.ti. or study.ti.)) or (nonrandom$ not random$).ti,ab. or "random field$".ti,ab. or (random cluster adj3 sampl$).ti,ab. or ((review.ab. and review.pt.) not trial.ti.) or ("we searched".ab. and (review.ti. or review.pt.)) or "update review".ab. or (databases adj4 searched).ab.))',
        exclude: null,
    },
    OBSERVATIONAL_RWE: {
        include: '(clinical study/ or case control study/ or family study/ or longitudinal study/ or retrospective study/ or (prospective study/ not randomized controlled trials/) or cohort analysis/ or (cohort adj (study or studies)).ti,ab,kw. or (case control adj (study or studies)).ti,ab,kw. or (follow up adj (study or studies)).ti,ab,kw. or (observational adj (study or studies)).ti,ab,kw. or (epidemiologic$ adj (study or studies)).ti,ab,kw. or (cross sectional adj (study or studies)).ti,ab,kw.)',
        exclude: '(case report/ or (case report or case reports or case series or case study or case studies).ti.)',
    },
    ECONOMIC: {
        include: '(Economics/ or Cost/ or exp Health Economics/ or Budget/ or budget$.ti,ab,kw. or (economic$ or cost or costs or costly or costing or price or prices or pricing or pharmacoeconomic$ or pharmaco-economic$ or expenditure or expenditures or expense or expenses or financial or finance or finances or financed).ti,kw. or (cost$ adj2 (effective$ or utilit$ or benefit$ or minimi$ or analy$ or outcome or outcomes)).ab,kw. or (value adj2 (money or monetary)).ti,ab,kw. or Statistical Model/ or economic model$.ab,kw. or Probability/ or markov.ti,ab,kw. or monte carlo method/ or monte carlo.ti,ab,kw. or Decision Theory/ or Decision Tree/ or (decision$ adj2 (tree$ or analy$ or model$)).ti,ab,kw.)',
        exclude: null,
    },
};

const buildOvidStudyDesignQuery = (keys) => {
    const includes = [];
    let needsExclude = false;
    (keys || []).forEach((key) => {
        const entry = OVID_STUDY_DESIGN_QUERY_MAP[key];
        if (!entry) return;
        includes.push(entry.include);
        if (entry.exclude) needsExclude = true;
    });
    if (!includes.length) return "";
    let combined = includes.length === 1 ? includes[0] : `(${includes.join(" or ")})`;
    if (needsExclude) combined = `${combined} not ${OVID_STUDY_DESIGN_QUERY_MAP.OBSERVATIONAL_RWE.exclude}`;
    return combined;
};

// publication_type keys: META_ANALYSIS | SYSTEMATIC_REVIEW | JOURNAL_ARTICLE | CASE_REPORT | GUIDELINES | CONFERENCE_ABSTRACT
const PUBLICATION_TYPE_OPTIONS = [
    { key: "META_ANALYSIS", label: "Meta-Analysis" },
    { key: "SYSTEMATIC_REVIEW", label: "Systematic Review" },
    { key: "JOURNAL_ARTICLE", label: "Journal Article" },
    { key: "CASE_REPORT", label: "Case Report" },
    { key: "GUIDELINES", label: "Guidelines" },
    { key: "CONFERENCE_ABSTRACT", label: "Conference Abstracts" },
];
const PUBLICATION_TYPE_FILTERS = PUBLICATION_TYPE_OPTIONS.map((o) => o.label);

// sex keys: FEMALE | MALE
const SEX_OPTIONS = [
    { key: "FEMALE", label: "Female" },
    { key: "MALE", label: "Male" },
];
const SEX_FILTERS = SEX_OPTIONS.map((o) => o.label);

// species keys: HUMANS | ANIMALS | ANIMALS_ONLY
const SPECIES_OPTIONS = [
    { key: "HUMANS", label: "Humans" },
    { key: "ANIMALS", label: "Other Animals" },
    { key: "ANIMALS_ONLY", label: "Animals Only" },
];
const SPECIES_FILTERS = SPECIES_OPTIONS.map((o) => o.label);

// language keys: ENGLISH | SPANISH | FRENCH | GERMAN
const LANGUAGE_OPTIONS = [
    { key: "ENGLISH", label: "English" },
    { key: "SPANISH", label: "Spanish" },
    { key: "FRENCH", label: "French" },
    { key: "GERMAN", label: "German" },
];
const LANGUAGE_FILTERS = LANGUAGE_OPTIONS.map((o) => o.label);

// geography keys — countries and regions
const GEO_OPTIONS = [
    // Countries
    { key: "UNITED_STATES", label: "United States" },
    { key: "UNITED_KINGDOM", label: "United Kingdom" },
    { key: "CANADA", label: "Canada" },
    { key: "GERMANY", label: "Germany" },
    { key: "FRANCE", label: "France" },
    { key: "INDIA", label: "India" },
    { key: "CHINA", label: "China" },
    { key: "JAPAN", label: "Japan" },
    { key: "BRAZIL", label: "Brazil" },
    { key: "AUSTRALIA", label: "Australia" },
    // Regions
    { key: "EUROPE", label: "Europe" },
    { key: "ASIA", label: "Asia" },
    { key: "NORTH_AMERICA", label: "North America" },
    { key: "SOUTH_AMERICA", label: "South America" },
    { key: "AFRICA", label: "Africa" },
    { key: "MIDDLE_EAST", label: "Middle East" },
    { key: "OCEANIA", label: "Oceania" },
];
const GEO_COUNTRIES = ["United States", "United Kingdom", "Canada", "Germany", "France", "India", "China", "Japan", "Brazil", "Australia"];
const GEO_REGIONS = ["Europe", "Asia", "North America", "South America", "Africa", "Middle East", "Oceania"];

/* Helper: label → backend key lookup (used in filter building) */
const optionKey = (opts, label) => opts.find((o) => o.label === label)?.key;
const optionLabel = (opts, key) => opts.find((o) => o.key === key)?.label;

/* Rewrites #N references in a final/combine query after steps are deleted or renumbered.
   `remap` maps original ref number → new number, or null if deleted.
   Single combined regex + single .replace() call is essential: two chained .replace() calls
   would have the second pass re-scan text already rewritten by the first (e.g. "#7"→"#6" then
   a second pass re-remapping that freshly-written "#6" again), corrupting the result on the
   2nd+ delete once shifted numbers collide with original ones. */
const remapQueryRefs = (query, remap) => {
    if (!query) return query;

    const result = query.replace(/\b(AND|OR|NOT)\s+#(\d+)\b|#(\d+)\b/g, (match, op, opNum, bareNum) => {
        const isOpForm = op !== undefined;
        const n = parseInt(isOpForm ? opNum : bareNum, 10);
        if (!(n in remap)) return match;
        const newN = remap[n];
        if (newN == null) return "";
        return isOpForm ? `${op} #${newN}` : `#${newN}`;
    });

    return result
        .replace(/\(\s*(AND|OR|NOT)\s+/gi, "(")
        .replace(/\s+(AND|OR|NOT)\s*\)/gi, ")")
        .replace(/\bAND\s+AND\b/gi, "AND")
        .replace(/\bOR\s+OR\b/gi, "OR")
        .replace(/\bAND\s+OR\b/gi, "OR")
        .replace(/\bOR\s+AND\b/gi, "AND")
        .replace(/^\s*(AND|OR|NOT)\s+/i, "")
        .replace(/\s+(AND|OR|NOT)\s*$/i, "")
        .replace(/\(\s*\)/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();
};

const TEXT_AVAILABILITY_FILTERS = [
    "Has Abstract",
    "Free Full Text",
    "Full Text",
];

const DATE_OPTIONS = [
    "no restriction on the publication timeframe",
    "Last 1 year",
    "Last 2 years",
    "Last 3 years",
    "Last 5 years",
    "Last 10 years",
    "Custom range",
];

const DB_LABELS = {
    PUBMED:           "PubMed",
    EMBASE:           "Embase",
    EMBASE_OVID:      "Embase (Ovid)",
    EMBASE_EMBASECOM: "Embase (Embase.com)",
    COCHRANE:         "Cochrane",
};

const EMBASE_PLATFORMS = [
    { key: "EMBASE_OVID",      label: "Embase (Ovid)" },
    { key: "EMBASE_EMBASECOM", label: "Embase (Embase.com)" },
];

const INPUT_METHODS = [
    { id: "research_question", label: "Research Query", icon: "bi-stars" },
    { id: "paste_query", label: "Custom Query", icon: "bi-code-square" },
];

/* ═══════════════════════════════════════════════════════════
   SMALL HELPERS
   ═══════════════════════════════════════════════════════════ */


const CheckboxGroup = ({ items, selected, onToggle, columns = 4 }) => (
    <div className="row g-2">
        {items.map((item) => (
            <div className={`col-${Math.max(1, Math.min(12, Math.floor(12 / columns)))}`} key={item}>
                <label className={`d-inline-flex align-items-center gap-2 cursor-pointer ev-small-p-dark ev-radius-xsmall ev-padding-xsmall ${selected.includes(item) ?
                    "" : ""}`}>
                    <input type="checkbox" checked={selected.includes(item)} onChange={() => onToggle(item)} />
                    {item}
                </label>
            </div>
        ))}
    </div>
);

const CharCountField = ({ label, required, optional, placeholder, value, onChange, maxLength = 500, rows = 3, disabled }) => {
    const pct = Math.min(100, Math.round(((value || "").length / maxLength) * 100));
    return (
        <div className="mb-3">
            <label className="ev-small-p-dark-blue ev-bold-med mb-1 d-block">
                {label}
                {required && <span className="text-danger"> *</span>}
                {optional && <span className="ev-small-p-blue ms-1">(Optional)</span>}
            </label>
            <textarea
                className="formField6 ev-radius-xsmall w-100"
                rows={rows}
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
                disabled={disabled}
            />
            <div className="d-flex justify-content-end align-items-center gap-2 mt-1">
                <span className={`ev-xsmall-p-blue ev-bold-reg ${pct >= 90 ? "ev-reg-p-red" : ""}`}>{(value || "").length} / {maxLength}</span>
            </div>
        </div>
    );
};

const SectionHeading = ({ number, title, badge, compact, desc }) => (
    <div className={`mb-3 pb-2 border-bottom ${compact ? "mt-0" : "mt-4"}`}>
        <div className="d-flex align-items-center gap-2">
            <span className="ev-message-card4 ev-bold-med ev-fs-11">{number}</span>
            <span className="ev-reg-p-dark-blue ev-bold-med">{title}</span>
            {badge && <span className="ev-message-card1 ev-fs-10">{badge}</span>}
        </div>
        {desc && <p className="ev-xsmall-p-dark mb-0 mt-1 ms-4 ps-1">{desc}</p>}
    </div>
);

const FilterSection = ({ label, children }) => (
    <div className="mb-3">
        <p className="ev-small-p-dark-blue ev-bold-med mb-2">{label}</p>
        {children}
    </div>
);

const FilterGroup = ({ id, label, count, open, onToggle, children, highlighted }) => (
    <div
        className="discovery-section mb-2"
        style={highlighted ? {
            boxShadow: "0 0 0 2px var(--color2)",
            borderRadius: "var(--px-8)",
            transition: "box-shadow 1.8s ease-out",
        } : { transition: "box-shadow 1.8s ease-out" }}
    >
        <button type="button" className="discovery-section-header w-100" onClick={() => onToggle(id)}>
            <span className="d-flex align-items-center gap-2">
                <span className="ev-small-p-dark-blue ev-bold-med mb-0">{label}</span>
                {count > 0 && <span className="ev-badge-pill ev-badge-blue ev-fs-10">{count}</span>}
            </span>
            <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11`}></i>
        </button>
        {open && <div className="discovery-section-body">{children}</div>}
    </div>
);

const CustomDropdown = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="position-relative" ref={wrapperRef}>
            <button
                type="button"
                className="discovery-section-header ev-radius-xsmall w-100 d-flex align-items-center justify-content-between text-start ev-small-p-dark"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <span className="ev-small-p-dark" style={{ opacity: value ? 1 : 0.6 }}>{value || placeholder}</span>
                <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11`}></i>
            </button>

            {isOpen && (
                <div
                    className="ev-card-4 ev-padding-xsmall ev-radius-xsmall w-100 scrollable-element mt-1"
                    style={{ maxHeight: "220px", overflowY: "auto" }}
                >
                    {value && (
                        <button
                            type="button"
                            className="w-100 text-start ev-radius-xsmall mb-1 ev-card-3 ev-small-p-dark border-0 ev-padding-xsmall"
                            style={{ opacity: 0.6 }}
                            onClick={() => { onChange(""); setIsOpen(false); }}
                        >
                            {placeholder}
                        </button>
                    )}
                    {options.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            className={`w-100 text-start ev-radius-xsmall mb-1 border-0 ev-padding-xsmall ${value === opt ? "ev-card-6 ev-bold-reg ev-small-p-dark" : "ev-card-3 ev-small-p-dark"}`}
                            onClick={() => { onChange(opt); setIsOpen(false); }}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ResizablePanels = ({ leftPanel, rightPanel }) => {
    const [leftWidth, setLeftWidth] = useState(50);
    const isDragging = useRef(false);
    const containerRef = useRef(null);

    const handleMouseDown = (e) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        const handleMouseMove = (e) => {
            if (!isDragging.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            if (rect.width === 0) return;
            const pct = ((e.clientX - rect.left) / rect.width) * 100;
            setLeftWidth(Math.max(25, Math.min(75, pct)));
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    return (
        <div ref={containerRef} className="d-flex mx-1 vh-75">
            <div style={{ width: `${leftWidth}%`, minWidth: 0 }} className="h-100">
                {leftPanel}
            </div>
            <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: 6, flexShrink: 0, cursor: "col-resize" }}
                onMouseDown={handleMouseDown}
            >
            </div>
            <div style={{ width: `${100 - leftWidth}%`, minWidth: 0 }} className="h-100">
                {rightPanel}
            </div>
        </div>
    );
};

const StepCard = ({ index, description, query, isFinal, isCustom, onEdit, onDelete, editing, onFieldChange, onSaveEdit }) => (
    <div className={`slr-step-card${isFinal ? " final" : ""}${isCustom ? " custom" : ""}`}>
        <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="d-flex align-items-center gap-2">
                <span className="slr-step-number">{index + 1}</span>
                <span className="ev-xsmall-p-dark-blue ev-bold-med mb-0">{`Step ${index + 1}`}</span>
                {isCustom && <span className="ev-message-card5 ev-fs-10">Manually added</span>}
                {isFinal && <span className="ev-message-card5 ev-fs-10">Final</span>}
            </div>
            {(onEdit || (!isFinal && onDelete)) && (
                <div className="d-flex gap-1">
                    {onEdit && (
                        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall" onClick={onEdit} title="Edit step">
                            <i className={`bi ${editing ? "bi-check2" : "bi-pencil"}`}></i>
                        </button>
                    )}
                    {!isFinal && onDelete && (
                        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall" onClick={onDelete} title="Remove step">
                            <i className="bi bi-trash ev-reg-p-red"></i>
                        </button>
                    )}
                </div>
            )}
        </div>

        {editing ? (
            <>
                <input
                    className="formField6 ev-radius-xsmall w-100 ev-fs-11 mt-2 mb-1"
                    value={description}
                    onChange={(e) => onFieldChange("description", e.target.value)}
                    placeholder="Description"
                />
                <textarea
                    className="formField6 ev-radius-xsmall w-100 ev-fs-11"
                    rows={2}
                    value={query}
                    onChange={(e) => onFieldChange("query", e.target.value)}
                    placeholder='e.g. ("term"[tiab])'
                />
                <div className="text-end mt-1">
                    <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-med" onClick={onSaveEdit}>Done</button>
                </div>
            </>
        ) : (
            <>
                <p className="ev-xsmall-p-dark mt-1 mb-1">{description}</p>
                <code className="ev-fs-11 d-block" style={{ color: "var(--color5)", wordBreak: "break-word" }}>{query || "—"}</code>
            </>
        )}
    </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */

const CreateLiteratureSearchForm = ({ onClose, onSuccess, editSearch, slrFabricId }) => {
    const navigate = useNavigate();

    const [searchName, setSearchName] = useState("");
    // Multi-select, but only along validated combinations (see
    // REVIEW_TYPE_COMPATIBILITY) — not a free-for-all. Empty array means
    // "not yet picked". Every action that would generate or save a search
    // is guarded on this being non-empty (see getReviewTypeGuardMessage).
    const [reviewTypes, setReviewTypes] = useState([]);
    // Transient message shown when the user tries an unsupported combination
    // (cleared on any subsequent valid toggle).
    const [reviewTypeWarning, setReviewTypeWarning] = useState("");
    const reviewTypeScrollRef = useRef(null);
    const [inputMethod, setInputMethod] = useState("research_question");
    const [researchQuestion, setResearchQuestion] = useState("");
    const [population, setPopulation] = useState("");
    const [intervention, setIntervention] = useState("");
    const [comparator, setComparator] = useState("");
    const [outcomes, setOutcomes] = useState("");
    const [studyDesignText, setStudyDesignText] = useState("");
    const [studyDesignQueryOverride, setStudyDesignQueryOverride] = useState("");
    const [studyDesignQueryEdited, setStudyDesignQueryEdited] = useState(false);
    // Separate override state for Embase.com — different syntax, different query
    // text, edited independently of PubMed's box above so the two never collide.
    const [embaseComStudyDesignQueryOverride, setEmbaseComStudyDesignQueryOverride] = useState("");
    const [embaseComStudyDesignQueryEdited, setEmbaseComStudyDesignQueryEdited] = useState(false);
    // Third, separate override state for Embase (Ovid) — same reasoning.
    const [ovidStudyDesignQueryOverride, setOvidStudyDesignQueryOverride] = useState("");
    const [ovidStudyDesignQueryEdited, setOvidStudyDesignQueryEdited] = useState(false);
    const [showStudyDesignQuery, setShowStudyDesignQuery] = useState(false);
    const [otherOutcomes, setOtherOutcomes] = useState("");
    const [aiPrompt, setAiPrompt] = useState("");
    const [pastedQuery, setPastedQuery] = useState("");
    const [selectedOutcomeTypes, setSelectedOutcomeTypes] = useState([]);
    const [otherOutcomeTypeText, setOtherOutcomeTypeText] = useState("");
    const [includeOutcomesInSearch, setIncludeOutcomesInSearch] = useState(false);
    const [includePubTypesInSearch, setIncludePubTypesInSearch] = useState(false);
    const [includeInterventionInSearch, setIncludeInterventionInSearch] = useState(false);
    const [selectedTextAvailability, setSelectedTextAvailability] = useState([]);
    const [selectedStudyDesigns, setSelectedStudyDesigns] = useState([]);

    // Reset any manual edit to the Study Design query preview whenever the selection changes,
    // so the editable box always starts from the fresh deterministic default for the new picks.
    // All three platforms' edit state reset together — a selection change invalidates all previews.
    useEffect(() => {
        setStudyDesignQueryEdited(false);
        setEmbaseComStudyDesignQueryEdited(false);
        setOvidStudyDesignQueryEdited(false);
    }, [selectedStudyDesigns]);
    const [selectedPubTypes, setSelectedPubTypes] = useState([]);
    const [selectedSex, setSelectedSex] = useState([]);
    const [selectedSpecies, setSelectedSpecies] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [hasAssociatedData, setHasAssociatedData] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [customGeoEntries, setCustomGeoEntries] = useState([]);
    const [customGeoInput, setCustomGeoInput] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [customDateFrom, setCustomDateFrom] = useState("");
    const [customDateTo, setCustomDateTo] = useState("");
    const [generatedQuery, setGeneratedQuery] = useState("");
    const [builtInputsSnapshot, setBuiltInputsSnapshot] = useState(null);
    const [builtConceptsSnapshot, setBuiltConceptsSnapshot] = useState(null); // {POPULATION, INTERVENTION, COMPARATOR, OUTCOMES} at last build
    const [isGenerating, setIsGenerating] = useState(false);
    const [isAutoFilling, setIsAutoFilling] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const [removedStepIds, setRemovedStepIds] = useState([]);
    const [customSteps, setCustomSteps] = useState([]);
    const [stepEdits, setStepEdits] = useState({});
    const [otherDbStepEdits, setOtherDbStepEdits] = useState({}); // { EMBASE_OVID: { [step_number]: {description, query_string} }, COCHRANE: {...} }
    const [otherDbRemovedSteps, setOtherDbRemovedSteps] = useState({}); // { EMBASE_OVID: [stepNumber, ...], COCHRANE: [...] }
    const [otherDbEditingStep, setOtherDbEditingStep] = useState(null);
    const [applyEditToOtherDbs, setApplyEditToOtherDbs] = useState(false);
    const [editingStepId, setEditingStepId] = useState(null);
    const [activeDatabase, setActiveDatabase] = useState("PUBMED");
    const [activeEmbasePlatform, setActiveEmbasePlatform] = useState("EMBASE_EMBASECOM"); // sub-tab when EMBASE is active — Embase.com is just the default; both sub-platforms have working generators now
    const [isBuilt, setIsBuilt] = useState(false);
    const [dbSteps, setDbSteps] = useState({});          // { PUBMED: {...}, EMBASE_OVID: {...}, EMBASE_EMBASECOM: {...}, COCHRANE: {...} }
    const [exportingDb, setExportingDb] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false); // true while a non-PubMed tab is regenerating on switch
    // True from the moment we know we're editing a saved discovery until its
    // saved steps AND additional_search_strategies have finished loading —
    // blocks handleSwitchDatabase/handleRebuildActiveTab from firing a
    // regeneration call that would race ahead of the restore and overwrite
    // (or duplicate-fetch) what's about to be hydrated in.
    const [isRestoringSavedSteps, setIsRestoringSavedSteps] = useState(Boolean(editSearch?.id));
    // isRegenerating (state) is only visible to the UI after a render commits,
    // so two rapid tab clicks (e.g. Embase then Cochrane before React has
    // re-rendered) can both read it as still-false and both fire a generate
    // call. This ref is checked-and-set synchronously in the same tick a
    // click handler runs, so the second click always sees the first one's
    // claim immediately, with no render in between.
    const generationInFlightRef = useRef(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [pendingImport, setPendingImport] = useState(null);      // validated rows awaiting user confirmation
    const exportMenuRef = useRef(null);
    const [openFilters, setOpenFilters] = useState({
        reviewType: true,
        outcomeTypes: true,
        textAvailability: false,
        studyDesign: true,
        pubType: false,
        demographics: false,
        additional: false,
        geography: false,
        date: true,
    });
    const [recentlyFilled, setRecentlyFilled] = useState({});
    const [justAutoFilled, setJustAutoFilled] = useState(false);

    // ═══ Prefill from editSearch (edit mode) ═══
    useEffect(() => {
        if (!editSearch) return;
        const answers = editSearch.form_answers || {};
        const filters = answers.filters || {};
        const pubDate = answers.publication_date || {};

        // Search name
        if (editSearch.name) setSearchName(editSearch.name);

        // Input method
        const methodMap = { RESEARCH_QUESTION: "research_question", MANUAL: "research_question", PASTE_QUERY: "paste_query" };
        setInputMethod(methodMap[editSearch.input_method] || "research_question");

        // Research question
        if (editSearch.research_question) setResearchQuestion(editSearch.research_question);

        // PICOS fields
        if (answers.REVIEW_TYPE) {
            // Legacy saved searches stored a single string; new ones store an array.
            setReviewTypes(Array.isArray(answers.REVIEW_TYPE) ? answers.REVIEW_TYPE : [answers.REVIEW_TYPE]);
        }
        if (answers.POPULATION) setPopulation(answers.POPULATION);
        if (answers.INTERVENTION) setIntervention(answers.INTERVENTION);
        if (answers.COMPARATOR) setComparator(answers.COMPARATOR);
        if (answers.OUTCOMES) setOutcomes(answers.OUTCOMES);
        if (answers.STUDY_DESIGN) setStudyDesignText(answers.STUDY_DESIGN);
        if (answers.OTHER_OUTCOMES) setOtherOutcomes(answers.OTHER_OUTCOMES);

        // Pasted query (for PASTE_QUERY mode)
        if (editSearch.input_method === "PASTE_QUERY" && editSearch.search_query) {
            setPastedQuery(editSearch.search_query);
            setGeneratedQuery(editSearch.search_query);
        }

        // Outcome types (stored as keys like "TREATMENT_PATTERNS" → need to find matching label)
        if (answers.OUTCOME_TYPES?.length) {
            const labels = answers.OUTCOME_TYPES.map((key) => {
                const match = OUTCOME_TYPES.find((o) => o.replace(/\s+/g, "_").toUpperCase() === key);
                return match || null;
            }).filter(Boolean);
            setSelectedOutcomeTypes(labels);
        }

        // Filters — keys → labels
        if (filters.study_design?.length) {
            setSelectedStudyDesigns(filters.study_design.map((k) => optionLabel(STUDY_DESIGN_OPTIONS, k)).filter(Boolean));
        }
        if (filters.publication_type?.length) {
            setSelectedPubTypes(filters.publication_type.map((k) => optionLabel(PUBLICATION_TYPE_OPTIONS, k)).filter(Boolean));
        }
        if (filters.species?.length) {
            setSelectedSpecies(filters.species.map((k) => optionLabel(SPECIES_OPTIONS, k)).filter(Boolean));
        }
        if (filters.sex?.length) {
            setSelectedSex(filters.sex.map((k) => optionLabel(SEX_OPTIONS, k)).filter(Boolean));
        }
        if (filters.language?.length) {
            setSelectedLanguages(filters.language.map((k) => optionLabel(LANGUAGE_OPTIONS, k)).filter(Boolean));
        }
        if (filters.text_availability?.length) {
            const taLabels = filters.text_availability.map((key) => {
                const match = TEXT_AVAILABILITY_FILTERS.find((t) => t.replace(/\s+/g, "_").toUpperCase() === key);
                return match || null;
            }).filter(Boolean);
            setSelectedTextAvailability(taLabels);
        }
        if (filters.has_associated_data) setHasAssociatedData(true);
        if (filters.geography?.length) {
            const known = [];
            const custom = [];
            filters.geography.forEach((k) => {
                const label = optionLabel(GEO_OPTIONS, k);
                if (label) known.push(label);
                else custom.push(k); // raw string from a previous custom entry
            });
            setSelectedCountries(known.filter((l) => GEO_COUNTRIES.includes(l)));
            setSelectedRegions(known.filter((l) => GEO_REGIONS.includes(l)));
            if (custom.length) setCustomGeoEntries(custom);
        }

        // Publication date
        if (pubDate.preset && pubDate.preset !== "NO_RESTRICTION") {
            if (pubDate.preset === "CUSTOM") {
                setDateFilter("Custom range");
                if (pubDate.from) setCustomDateFrom(String(pubDate.from).slice(0, 4));
                if (pubDate.to) setCustomDateTo(String(pubDate.to).slice(0, 4));
            } else {
                const match = DATE_OPTIONS.find((d) => d.replace(/\s+/g, "_").toUpperCase() === pubDate.preset);
                if (match) setDateFilter(match);
            }
        }

        if (answers.include_flags) {
            if (typeof answers.include_flags.outcomes === "boolean") setIncludeOutcomesInSearch(answers.include_flags.outcomes);
            if (typeof answers.include_flags.publication_type === "boolean") setIncludePubTypesInSearch(answers.include_flags.publication_type);
            if (typeof answers.include_flags.intervention === "boolean") setIncludeInterventionInSearch(answers.include_flags.intervention);
        }

        // Database
        if (answers.active_database) setActiveDatabase(answers.active_database);

        // Mark as already built so the query is shown
        const savedQuery = editSearch.search_query || editSearch.query;
        if (savedQuery) {
            setGeneratedQuery(savedQuery);
            setIsBuilt(true);
        }
    }, [editSearch]);

    // ═══ Hydrate saved steps in edit mode ═══
    // Without this the right-hand panel opens empty, and saving would send an
    // empty steps array — save_search_form deletes and re-inserts the whole
    // set, so the user's saved steps would be wiped.
    useEffect(() => {
        if (!editSearch?.id) return;
        let cancelled = false;
        setIsRestoringSavedSteps(true);

        (async () => {
            try {
                const res = await axios.post(
                    `${API_BASE_URL}/hits-summary`,
                    { discovery_id: editSearch.id, page: 1, size: 1000 },
                    { headers }
                );
                if (cancelled) return;
                if (res.data?.status === "NotAuthorized") {
                    authNavigate("/login", "iam", navigate);
                    return;
                }

                // Restore the OTHER platforms' already-generated strategies
                // (EMBASE_OVID / EMBASE_EMBASECOM / COCHRANE) so switching to
                // those tabs shows the saved content instead of triggering a
                // fresh AI regeneration. Independent of the primary-platform
                // steps below, so it still runs even if those are empty.
                const additional = res.data?.data?.additional_search_strategies;
                if (additional && typeof additional === "object") {
                    const { PUBMED: _ignoredPubmed, ...restoredOtherDbSteps } = additional;
                    if (Object.keys(restoredOtherDbSteps).length) {
                        setDbSteps((prev) => ({ ...prev, ...restoredOtherDbSteps }));
                    }
                }

                const steps = res.data?.data?.steps || [];
                if (!steps.length) return;

                setCustomSteps(steps.map((s) => ({
                    id: `saved-${s.step_number}`,
                    description: s.description || `Step ${s.step_number}`,
                    query: s.query_string || "",
                    auto: (s.source || "AI") === "AI",
                    stepRole: s.step_role,
                    isFinal: s.is_final,
                    originKey: s.origin_key,
                })));
                setRemovedStepIds([]);
                setStepEdits({});
                setIsBuilt(true);
                setBuiltInputsSnapshot(currentInputsFingerprint);
            } catch (err) {
                console.error("Could not load saved steps for edit", err);
            } finally {
                if (!cancelled) setIsRestoringSavedSteps(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editSearch?.id]);

    const API_BASE_URL = "/pceapi/slr/discovery";
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);
    const isProcessing = isGenerating || isCreating || isAutoFilling;
    const isEditMode = Boolean(editSearch?.id);

    const toggleItem = (setter) => (item) => {
        setter((prev) => prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]);
    };

    const toggleFilterOpen = (id) => setOpenFilters((prev) => ({ ...prev, [id]: !prev[id] }));

    const clearForm = () => {
        setSearchName("");
        setResearchQuestion(""); setPopulation(""); setIntervention(""); setComparator("");
        setOutcomes(""); setStudyDesignText(""); setOtherOutcomes(""); setAiPrompt("");
        setPastedQuery(""); setSelectedOutcomeTypes([]); setSelectedTextAvailability([]);
        setSelectedStudyDesigns([]); setSelectedPubTypes([]); setSelectedSex([]);
        setSelectedSpecies([]); setSelectedLanguages([]); setHasAssociatedData(false);
        setSelectedCountries([]); setSelectedRegions([]); setCustomGeoEntries([]); setCustomGeoInput(""); setDateFilter("");
        setCustomDateFrom(""); setCustomDateTo(""); setGeneratedQuery(""); setBuiltInputsSnapshot(null); setBuiltConceptsSnapshot(null);
        setRemovedStepIds([]); setCustomSteps([]); setStepEdits({}); setEditingStepId(null);
        setOtherDbStepEdits({}); setOtherDbRemovedSteps({}); setOtherDbEditingStep(null); setApplyEditToOtherDbs(false);
        setIncludeOutcomesInSearch(false); setIncludePubTypesInSearch(false); setIncludeInterventionInSearch(false);
        setActiveDatabase("PUBMED"); setActiveEmbasePlatform("EMBASE_OVID"); setIsBuilt(false); setOtherOutcomeTypeText("");
        setDbSteps({}); setExportingDb(null); setIsRegenerating(false);
        setPendingImport(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const activeSteps = useMemo(() => {
        const merged = customSteps.filter((s) => !removedStepIds.includes(s.id));
        return merged.map((s) => (stepEdits[s.id] ? { ...s, ...stepEdits[s.id] } : s));
    }, [customSteps, removedStepIds, stepEdits]);

    // Resolves to the actual dbSteps key for the current view:
    // EMBASE → whichever sub-tab (EMBASE_OVID / EMBASE_EMBASECOM) is active
    // everything else → the activeDatabase key directly
    const activePlatformKey = activeDatabase === "EMBASE" ? activeEmbasePlatform : activeDatabase;

    const activeOtherDbSteps = useMemo(() => {
        const raw = dbSteps[activePlatformKey]?.steps || [];
        const edits = otherDbStepEdits[activePlatformKey] || {};
        const removed = otherDbRemovedSteps[activePlatformKey] || [];
        return raw
            .filter((s) => !removed.includes(s.step_number))
            .map((s) => (edits[s.step_number] ? { ...s, ...edits[s.step_number] } : s));
    }, [dbSteps, activePlatformKey, otherDbStepEdits, otherDbRemovedSteps]);

    /* Finds the step_number that represents the same concept (origin_key) in a different
       database's raw step list — never a final step (origin_key is always null on a final
       step, so this naturally excludes it). Databases can have different step counts/ordering,
       so matching across databases by raw step_number is unsafe (can accidentally hit another
       db's final step if numbers happen to coincide) — origin_key is the only safe identity. */
    const findStepNumberByOriginKey = (dbKey, originKey) => {
        if (!originKey) return null;
        const match = dbSteps[dbKey]?.steps?.find((s) => s.origin_key === originKey && !s.is_final);
        return match ? match.step_number : null;
    };

    const handleSaveOtherDbStepEdit = (stepNumber, description, queryText) => {
        const patch = { description, query_string: queryText };
        const sourceStep = dbSteps[activePlatformKey]?.steps?.find((s) => s.step_number === stepNumber);
        const originKey = sourceStep?.origin_key;

        setOtherDbStepEdits((prev) => {
            const next = { ...prev };
            next[activePlatformKey] = { ...(next[activePlatformKey] || {}), [stepNumber]: patch };

            if (applyEditToOtherDbs && originKey) {
                Object.keys(dbSteps).filter((db) => db !== "PUBMED" && db !== activePlatformKey).forEach((db) => {
                    const targetStepNumber = findStepNumberByOriginKey(db, originKey);
                    if (targetStepNumber != null) {
                        next[db] = { ...(next[db] || {}), [targetStepNumber]: patch };
                    }
                });
            }
            return next;
        });
        setOtherDbEditingStep(null);
        setApplyEditToOtherDbs(false);
    };

    // Fingerprint of all inputs that feed into the build — used to detect real changes
    const currentInputsFingerprint = useMemo(() => JSON.stringify([
        population, intervention, comparator, outcomes,
        selectedOutcomeTypes, selectedStudyDesigns, selectedPubTypes,
        selectedSpecies, selectedSex, selectedLanguages,
        selectedCountries, selectedRegions, customGeoEntries,
        dateFilter, customDateFrom, customDateTo,
    ]), [
        population, intervention, comparator, outcomes,
        selectedOutcomeTypes, selectedStudyDesigns, selectedPubTypes,
        selectedSpecies, selectedSex, selectedLanguages,
        selectedCountries, selectedRegions, customGeoEntries,
        dateFilter, customDateFrom, customDateTo,
    ]);

    const queryOutOfDate = builtInputsSnapshot !== null && builtInputsSnapshot !== currentInputsFingerprint;

    // Which PICOS concepts changed since the last build — used for selective step update
    const changedConcepts = useMemo(() => {
        if (!builtConceptsSnapshot || !isBuilt) return [];
        const changed = [];
        if (builtConceptsSnapshot.POPULATION !== population.trim()) changed.push("Population");
        if (builtConceptsSnapshot.INTERVENTION !== intervention.trim()) changed.push("Intervention");
        if (builtConceptsSnapshot.COMPARATOR !== comparator.trim()) changed.push("Comparator");
        if (builtConceptsSnapshot.OUTCOMES !== outcomes.trim()) changed.push("Outcomes");
        return changed;
    }, [builtConceptsSnapshot, population, intervention, comparator, outcomes, isBuilt]);

    // After import populates form fields, state updates are async — capture the new fingerprint
    // as the baseline so subsequent edits correctly trigger the rebuild warning
    useEffect(() => {
        if (justImportedRef.current) {
            setBuiltInputsSnapshot(currentInputsFingerprint);
            setBuiltConceptsSnapshot({
                POPULATION: population.trim(),
                INTERVENTION: intervention.trim(),
                COMPARATOR: comparator.trim(),
                OUTCOMES: outcomes.trim(),
            });
            justImportedRef.current = false;
        }
    }, [currentInputsFingerprint, population, intervention, comparator, outcomes]);

    // Invalidate cached non-PubMed steps when Population changes — Population
    // defines the disease, so a change means all prior steps are stale.
    useEffect(() => {
        if (isBuilt && builtConceptsSnapshot &&
            builtConceptsSnapshot.POPULATION !== population.trim()) {
            setDbSteps({});
        }
    }, [population]);

    const handleAddCustomStep = () => {
        const id = `custom-${Date.now()}`;
        setCustomSteps((prev) => [...prev, { id, description: "Custom step", query: "", auto: false }]);
        setEditingStepId(id);
    };
    const handleRemoveStep = (id) => {
        setRemovedStepIds((prev) => [...prev, id]);
        const finalStep = customSteps.find((s) => s.isFinal);
        if (finalStep?.id && stepEdits[finalStep.id]?.query !== undefined) {
            setStepEdits((prev) => {
                const next = { ...prev };
                const { query, ...rest } = next[finalStep.id] || {};
                if (Object.keys(rest).length > 0) {
                    next[finalStep.id] = rest;   
                } else {
                    delete next[finalStep.id];
                }
                return next;
            });
        }

        const sourceStep = customSteps.find((s) => s.id === id);
        const originKey = sourceStep?.originKey;
        if (!originKey) return;

        setOtherDbRemovedSteps((prev) => {
            const next = { ...prev };
            Object.keys(dbSteps).filter((db) => db !== "PUBMED").forEach((db) => {
                const targetStepNumber = findStepNumberByOriginKey(db, originKey);
                if (targetStepNumber != null) {
                    next[db] = [...new Set([...(next[db] || []), targetStepNumber])];
                }
            });
            return next;
        });
    };
    const handleStepFieldChange = (id, field, value) => setStepEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

    // Recompute final step query when non-final steps are removed.
    // The backend's final_query uses #N references (e.g. #1 AND #2 AND #3).
    // When a step is deleted, remaining steps get renumbered — this remap
    // updates the final query's #N refs to match the new numbering.
    const derivedFinalQuery = useMemo(() => {
        const finalStep = activeSteps.find(s => s.isFinal);
        // If the user manually edited the final step's query, return their value as-is
        // (skip remap — they've taken explicit control of the combine query)
        if (finalStep?.id && stepEdits[finalStep.id]?.query !== undefined) {
            return stepEdits[finalStep.id].query;
        }
        const baseQuery = (finalStep?.query || generatedQuery || "").trim();
        if (!baseQuery || removedStepIds.length === 0) return baseQuery;

        // All non-final steps in original build order
        const allNonFinal = customSteps.filter(s => !s.isFinal);
        // Active (non-removed) non-final steps in same order
        const activeNonFinal = activeSteps.filter(s => !s.isFinal);

        // Build remap: original 1-based position → new 1-based position (null = deleted)
        const remap = {};
        allNonFinal.forEach((s, i) => {
            const newIdx = activeNonFinal.findIndex(a => a.id === s.id);
            remap[i + 1] = newIdx >= 0 ? newIdx + 1 : null;
        });

        return remapQueryRefs(baseQuery, remap);
    }, [activeSteps, customSteps, removedStepIds, generatedQuery, stepEdits]);

    /* ═══════════════════════════════════════════════════════════
       INTEGRATED ENDPOINTS
       ═══════════════════════════════════════════════════════════ */

    /* Review Type no longer pre-selects anything. It only determines which
       Study Design / Outcome Type options are VISIBLE (see the filtering in
       renderOutcomeTypes and the Study Design FilterGroup render, both keyed
       off REVIEW_TYPE_OUTCOME_MAP / REVIEW_TYPE_STUDY_DESIGN_VISIBLE).
       Switching Review Type clears whatever was previously selected in
       these categories — since the old picks may no longer even be visible
       for the new Review Type — but leaves a genuinely empty slate for the
       user to fill in manually, or via Auto-fill, rather than guessing on
       their behalf. Publication Type is cleared the same way for consistency,
       even though Treatment Guidelines/Patterns is the only Review Type that
       ever related to it. */
    const clearFilterSelectionsOnReviewTypeChange = () => {
        setSelectedOutcomeTypes([]);
        setSelectedStudyDesigns([]);
        setSelectedPubTypes([]);
    };

    /* Toggle a Review Type on/off. Multi-select, but restricted to the
       validated combinations in REVIEW_TYPE_COMPATIBILITY — clicking a
       second type that isn't compatible with what's already selected is
       blocked (shows a transient inline message) rather than silently
       swapping or allowing an unvalidated combination. Max group size is 2. */
    const handleToggleReviewType = (key) => {
        setReviewTypes((prev) => {
            if (prev.includes(key)) {
                const next = prev.filter((k) => k !== key);
                clearFilterSelectionsOnReviewTypeChange();
                setReviewTypeWarning("");
                return next;
            }

            if (prev.length === 0) {
                clearFilterSelectionsOnReviewTypeChange();
                setReviewTypeWarning("");
                return [key];
            }

            const allCompatible = prev.every((existing) => (REVIEW_TYPE_COMPATIBILITY[existing] || []).includes(key));
            if (!allCompatible || prev.length >= 2) {
                const existingLabels = prev.map((k) => REVIEW_TYPES.find((r) => r.key === k)?.label || k).join(" + ");
                const newLabel = REVIEW_TYPES.find((r) => r.key === key)?.label || key;
                setReviewTypeWarning(
                    prev.length >= 2 && allCompatible
                        ? `Only two Review Types can be combined at a time. Remove one before adding "${newLabel}".`
                        : `"${newLabel}" isn't a supported combination with "${existingLabels}". Deselect it first, or pick a compatible pair.`
                );
                return prev; // reject — unchanged
            }

            const next = [...prev, key];
            clearFilterSelectionsOnReviewTypeChange();
            setReviewTypeWarning("");
            return next;
        });
    };

    /* Central guard used by every action that would generate or save a
       search (auto-fill, rebuild on any tab, tab-switch auto-regenerate,
       save). Returns null when everything's consistent, or a warning
       string to show and block on otherwise. "Other" is neutral and never
       counted as a mismatch against any selected Review Type. */
    const getReviewTypeGuardMessage = () => {
        if (!reviewTypes.length) {
            return "Please select a Review Type before continuing.";
        }
        const allowed = reviewTypes.flatMap((k) => REVIEW_TYPE_OUTCOME_MAP[k] || []);
        const offending = selectedOutcomeTypes.filter((o) => o !== "Other" && !allowed.includes(o));
        if (offending.length) {
            const reviewLabel = reviewTypes.map((k) => REVIEW_TYPES.find((r) => r.key === k)?.label || k).join(" + ");
            return `Selected Outcome Type(s) don't match "${reviewLabel}": ${offending.join(", ")}. ` +
                   `Please align your Outcome Types with the Review Type, or change the Review Type.`;
        }
        return null;
    };

    // 1. ENDPOINT: /auto-fill-form
    const handleAutoFill = async () => {
        if (!researchQuestion.trim() || isAutoFilling) return;
        const guardMsg = getReviewTypeGuardMessage();
        if (guardMsg) {
            setPopupErrorMessage(guardMsg);
            return;
        }
        setIsAutoFilling(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Auto-filling form from your question...");
        setPopupErrorMessage("");

        try {
            // Auto-fill can only choose among options that are actually VISIBLE
            // for the selected Review Type(s) — same filtering renderOutcomeTypes
            // and the Study Design FilterGroup apply for manual selection, so the
            // AI never picks something the user wouldn't even see as a checkbox.
            const allowedOutcomesForReview = reviewTypes.length
                ? OUTCOME_TYPES.filter((o) => o === "Other" || reviewTypes.some((k) => (REVIEW_TYPE_OUTCOME_MAP[k] || []).includes(o)))
                : OUTCOME_TYPES;
            const visibleStudyDesignKeysForReview = reviewTypes.length
                ? [...new Set(reviewTypes.flatMap((k) => REVIEW_TYPE_STUDY_DESIGN_VISIBLE[k] || []))]
                : STUDY_DESIGN_OPTIONS.map((o) => o.key);

            const fields = [
                { key: "POPULATION", label: "Population", type: "TEXT", required: true, hint: "who the studies are about" },
                { key: "INTERVENTION", label: "Intervention / Exposure", type: "TEXT", required: false, hint: "treatment or exposure being studied" },
                { key: "COMPARATOR", label: "Comparator", type: "TEXT", required: false, hint: "comparison treatment or control" },
                { key: "OUTCOMES", label: "Outcomes", type: "TEXT", required: false, hint: "what is measured" },
                { key: "OTHER_OUTCOMES", label: "Other Outcomes", type: "LONG_TEXT", required: false, hint: "biomarkers, patient-reported, etc." },
                { key: "STUDY_DESIGN", label: "Study Design", type: "TEXT", required: false, hint: "e.g. RCT, observational" },
                { key: "OUTCOME_TYPES", label: "Outcome Types", type: "MULTI_SELECT", required: false, options: allowedOutcomesForReview.map((o) => ({ key: o.replace(/\s+/g, "_").toUpperCase(), label: o })) },
                { key: "STUDY_DESIGN_FILTER", label: "Study Design Filter", type: "MULTI_SELECT", required: false, options: STUDY_DESIGN_OPTIONS.filter((o) => visibleStudyDesignKeysForReview.includes(o.key)).map((o) => ({ key: o.key, label: o.label })) },
                { key: "PUBLICATION_TYPE", label: "Publication Type", type: "MULTI_SELECT", required: false, options: PUBLICATION_TYPE_OPTIONS.map((o) => ({ key: o.key, label: o.label })) },
                { key: "SPECIES", label: "Species", type: "MULTI_SELECT", required: false, options: SPECIES_OPTIONS.map((o) => ({ key: o.key, label: o.label })) },
                { key: "LANGUAGE", label: "Language", type: "MULTI_SELECT", required: false, options: LANGUAGE_OPTIONS.map((o) => ({ key: o.key, label: o.label })) },
                { key: "PUBLICATION_DATE", label: "Publication Date", type: "DATE_PRESET", required: false, options: DATE_OPTIONS.map((d) => ({ key: d.replace(/\s+/g, "_").toUpperCase(), label: d })) },
                { key: "GEOGRAPHY", label: "Geographic Filter", type: "MULTI_SELECT", required: false, options: GEO_OPTIONS.map((o) => ({ key: o.key, label: o.label })) },
            ];

            const res = await axios.post(`${API_BASE_URL}/auto-fill-form`, {
                natural_language_query: researchQuestion.trim(),
                fields: fields,
                framework: "PICOS",
                review_type: reviewTypes.map((k) => REVIEW_TYPES.find((r) => r.key === k)?.label).filter(Boolean).join(" + ") || null,
            }, { headers });

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                const answers = res.data.data?.answers || res.data.answers || {};
                const groupsToOpen = {};
                if (answers.POPULATION) setPopulation(answers.POPULATION);
                if (answers.INTERVENTION) setIntervention(answers.INTERVENTION);
                if (answers.COMPARATOR) setComparator(answers.COMPARATOR);
                if (answers.OUTCOMES) setOutcomes(answers.OUTCOMES);
                if (answers.OTHER_OUTCOMES) setOtherOutcomes(answers.OTHER_OUTCOMES);
                if (answers.STUDY_DESIGN) setStudyDesignText(answers.STUDY_DESIGN);
                if (answers.OUTCOME_TYPES && Array.isArray(answers.OUTCOME_TYPES)) {
                    const mapped = answers.OUTCOME_TYPES.map((k) => OUTCOME_TYPES.find((o) => o.replace(/\s+/g, "_").toUpperCase() === k)).filter(Boolean);
                    if (mapped.length) { setSelectedOutcomeTypes(mapped); groupsToOpen.outcomeTypes = true; }
                }
                if (answers.STUDY_DESIGN_FILTER && Array.isArray(answers.STUDY_DESIGN_FILTER)) {
                    const mapped = answers.STUDY_DESIGN_FILTER.map((k) => STUDY_DESIGN_OPTIONS.find((o) => o.key === k)?.label).filter(Boolean);
                    if (mapped.length) { setSelectedStudyDesigns(mapped); groupsToOpen.studyDesign = true; }
                }
                if (answers.PUBLICATION_TYPE && Array.isArray(answers.PUBLICATION_TYPE)) {
                    const mapped = answers.PUBLICATION_TYPE.map((k) => PUBLICATION_TYPE_OPTIONS.find((o) => o.key === k)?.label).filter(Boolean);
                    if (mapped.length) { setSelectedPubTypes(mapped); groupsToOpen.pubType = true; }
                }
                if (answers.SPECIES && Array.isArray(answers.SPECIES)) {
                    const mapped = answers.SPECIES.map((k) => SPECIES_OPTIONS.find((o) => o.key === k)?.label).filter(Boolean);
                    if (mapped.length) { setSelectedSpecies(mapped); groupsToOpen.demographics = true; }
                }
                if (answers.LANGUAGE && Array.isArray(answers.LANGUAGE)) {
                    const mapped = answers.LANGUAGE.map((k) => LANGUAGE_OPTIONS.find((o) => o.key === k)?.label).filter(Boolean);
                    if (mapped.length) { setSelectedLanguages(mapped); groupsToOpen.demographics = true; }
                }
                if (answers.GEOGRAPHY && Array.isArray(answers.GEOGRAPHY)) {
                    const knownLabels = answers.GEOGRAPHY.map((k) => optionLabel(GEO_OPTIONS, k)).filter(Boolean);
                    const countries = knownLabels.filter((l) => GEO_COUNTRIES.includes(l));
                    const regions = knownLabels.filter((l) => GEO_REGIONS.includes(l));
                    if (countries.length) { setSelectedCountries(countries); groupsToOpen.geography = true; }
                    if (regions.length) { setSelectedRegions(regions); groupsToOpen.geography = true; }
                }
                if (answers.PUBLICATION_DATE && typeof answers.PUBLICATION_DATE === "object") {
                    const preset = answers.PUBLICATION_DATE.preset;
                    if (preset === "CUSTOM" || preset === "CUSTOM_RANGE") {
                        setDateFilter("Custom range");
                        if (answers.PUBLICATION_DATE.from) setCustomDateFrom(String(answers.PUBLICATION_DATE.from).slice(0, 4));
                        if (answers.PUBLICATION_DATE.to) setCustomDateTo(String(answers.PUBLICATION_DATE.to).slice(0, 4));
                        groupsToOpen.date = true;
                    } else if (preset) {
                        const label = DATE_OPTIONS.find((d) => d.replace(/\s+/g, "_").toUpperCase() === preset);
                        if (label) { setDateFilter(label); groupsToOpen.date = true; }
                    }
                }

                if (Object.keys(groupsToOpen).length) {
                    setOpenFilters((prev) => ({ ...prev, ...groupsToOpen }));
                    setRecentlyFilled(groupsToOpen);
                    setTimeout(() => setRecentlyFilled({}), 1800);
                }
                setJustAutoFilled(true);
                setTimeout(() => setJustAutoFilled(false), 2600);

                setPopupSuccessMessage("Form auto-filled successfully");
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to auto-fill");
            }
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(error.response?.data?.message || error?.message || "Error auto-filling form");
        } finally {
            setIsAutoFilling(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* Builds the /generate-steps request body from current PICOS + filters. Returns null if Population is empty. */
    const buildGenerateStepsPayload = () => {
        const concept_groups = [];
        if (population.trim()) {
            concept_groups.push({
                group_label: "Population",
                items: [{ key: "POPULATION", label: "Population", value: population.trim() }],
            });
        }
        const interventionGroup = [];
        if (includeInterventionInSearch && intervention.trim()) {
            interventionGroup.push({ key: "INTERVENTION", label: "Intervention / Exposure", value: intervention.trim() });
        }
        if (includeInterventionInSearch && comparator.trim()) {
            interventionGroup.push({ key: "COMPARATOR", label: "Comparator", value: comparator.trim() });
        }
        if (interventionGroup.length) {
            concept_groups.push({ group_label: "Intervention + Comparator", items: interventionGroup });
        }
        const outcomeItems = [];
        if (includeOutcomesInSearch && outcomes.trim()) {
            outcomeItems.push({ key: "OUTCOMES", label: "Outcomes", value: outcomes.trim() });
        }
        if (includeOutcomesInSearch && otherOutcomes.trim()) {
            outcomeItems.push({ key: "OTHER_OUTCOMES", label: "Other Outcomes", value: otherOutcomes.trim() });
        }
        if (outcomeItems.length) {
            concept_groups.push({ group_label: "Outcomes", items: outcomeItems });
        }
        if (concept_groups.length === 0) return null;

        const outcome_types = includeOutcomesInSearch
            ? selectedOutcomeTypes.flatMap((o) => {
                if (o === "Other") {
                    const txt = otherOutcomeTypeText.trim();
                    return txt ? [{ key: "OTHER", label: txt }] : [];
                }
                return [{ key: o.replace(/\s+/g, "_").toUpperCase(), label: o }];
            })
            : [];

        const filters = {};
        const studyDesignKeys = selectedStudyDesigns.map((l) => optionKey(STUDY_DESIGN_OPTIONS, l)).filter(Boolean);
        if (studyDesignKeys.length) filters.study_design = studyDesignKeys;
        const studyDesignQuery = studyDesignKeys.length
            ? (studyDesignQueryEdited ? studyDesignQueryOverride : buildStudyDesignQuery(studyDesignKeys))
            : "";
        if (includePubTypesInSearch && selectedPubTypes.length) filters.publication_type = selectedPubTypes.map((l) => optionKey(PUBLICATION_TYPE_OPTIONS, l)).filter(Boolean);
        if (selectedSpecies.length) filters.species = selectedSpecies.map((l) => optionKey(SPECIES_OPTIONS, l)).filter(Boolean);
        if (selectedSex.length) filters.sex = selectedSex.map((l) => optionKey(SEX_OPTIONS, l)).filter(Boolean);
        if (selectedLanguages.length) filters.language = selectedLanguages.map((l) => optionKey(LANGUAGE_OPTIONS, l)).filter(Boolean);
        if (selectedCountries.length || selectedRegions.length || customGeoEntries.length) {
            filters.geography = [
                ...[...selectedCountries, ...selectedRegions].map((l) => optionKey(GEO_OPTIONS, l)).filter(Boolean),
                ...customGeoEntries,
            ];
        }

        let publication_date = { preset: "NO_RESTRICTION", from: null, to: null };
        if (dateFilter === "Custom range") {
            publication_date = { preset: "CUSTOM", from: customDateFrom || null, to: customDateTo || null };
        } else if (dateFilter) {
            publication_date = { preset: dateFilter.replace(/\s+/g, "_").toUpperCase(), from: null, to: null };
        }

        return { concept_groups, outcome_types, filters, publication_date, study_design_query: studyDesignQuery };
    };

    /* Shared call to /generate-steps. applyToPubmed=true (Build/Rebuild) also refreshes the editable
       PubMed step cards; applyToPubmed=false (tab-switch regenerate) only refreshes dbSteps so any
       manual edits on the PubMed side are left untouched. */
    const runGenerateSteps = async ({ applyToPubmed }) => {
        const body = buildGenerateStepsPayload();
        if (!body) {
            setPopupErrorMessage("At least Population is required to build search strings");
            return false;
        }

        try {
            const res = await axios.post(
                `${API_BASE_URL}/generate-steps`,
                { ...body, research_question: researchQuestion.trim() || null },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return false;
            }

            if (res.data?.status === "success") {
                const data = res.data.data || {};
                const databases = data.databases || {};
                const pubmedPayload = databases.PUBMED;

                if (applyToPubmed && pubmedPayload?.steps?.length > 0) {
                    const aiSteps = pubmedPayload.steps.map((step) => ({
                        id: `ai-${step.step_number}-${Date.now()}`,
                        description: step.description || `Step ${step.step_number}`,
                        query: step.query_string || "",
                        auto: true,
                        stepRole: step.step_role,
                        isFinal: step.is_final,
                        originKey: step.origin_key,
                    }));

                    // Selective merge: if rebuilding and we know which concepts changed,
                    // keep unchanged-concept steps from the existing set; always take new final step
                    const changedOriginKeys = changedConcepts.map((c) => c.toUpperCase());
                    const existingLive = customSteps.filter((s) => !removedStepIds.includes(s.id));

                    if (isBuilt && changedConcepts.length > 0
                        && changedConcepts.length < 4
                        && !changedConcepts.includes("Population")
                        && existingLive.length > 0) {
                        // Index existing non-final steps by originKey (queue per key for multi-step concepts)
                        const existingByOrigin = {};
                        existingLive.forEach((s) => {
                            if (!s.isFinal && s.originKey) {
                                existingByOrigin[s.originKey] = existingByOrigin[s.originKey] || [];
                                existingByOrigin[s.originKey].push({ ...s });
                            }
                        });

                        // Walk the new steps in order (preserves step numbering for #N refs in final step)
                        const merged = aiSteps.map((newStep) => {
                            if (newStep.isFinal) return newStep; // always use new final step
                            // Filter / Study-Design steps must always be replaced by the freshly
                            // generated step — never merged with a cached one. changedOriginKeys
                            // only tracks Population/Intervention/Comparator/Outcomes, so without
                            // this early return a checkbox-only change (e.g. ticking ECONOMIC)
                            // would be masked and a stale filter step would survive the rebuild.
                            if (newStep.stepRole === "FILTER" || newStep.stepRole === "STUDY_DESIGN") {
                                return newStep;
                            }
                            const ok = newStep.originKey;
                            if (ok && !changedOriginKeys.includes(ok) && existingByOrigin[ok]?.length > 0) {
                                return existingByOrigin[ok].shift(); // keep user's existing step for this concept
                            }
                            return newStep; // changed or new concept — use AI step
                        });

                        setCustomSteps(merged);
                    } else {
                        setCustomSteps(aiSteps);
                    }

                    setRemovedStepIds([]);
                    setStepEdits({});
                    setGeneratedQuery(pubmedPayload.final_query || "");
                }

                if (applyToPubmed) {
                    setOtherDbStepEdits({});
                    setOtherDbRemovedSteps({});
                    setOtherDbEditingStep(null);
                    setApplyEditToOtherDbs(false);
                }

                setDbSteps(databases);
                setBuiltInputsSnapshot(currentInputsFingerprint);
                setBuiltConceptsSnapshot({
                    POPULATION: population.trim(),
                    INTERVENTION: intervention.trim(),
                    COMPARATOR: comparator.trim(),
                    OUTCOMES: outcomes.trim(),
                });
                setIsBuilt(true);

                const failedDbs = Object.keys(data.failed_databases || {});
                if (failedDbs.length) {
                    setPopupErrorMessage(`Could not generate for: ${failedDbs.map((d) => DB_LABELS[d] || d).join(", ")}`);
                }
                return true;
            }
            setPopupErrorMessage(res.data?.message || "Failed to generate search steps");
            return false;
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return false;
            }
            setPopupErrorMessage(error.response?.data?.message || error.message || "Failed to generate search steps");
            return false;
        }
    };

    // 2. ENDPOINT: /generate-steps
    const handleBuildStrings = async () => {
        if (isProcessing) return;
        setIsGenerating(true);
        setIsProcessingPopupVisible(true);
        const msg = changedConcepts.length > 0
            ? `Updating ${changedConcepts.join(" and ")} step${changedConcepts.length > 1 ? "s" : ""} using AI...`
            : "Generating search steps with AI...";
        setProcessingMessage(msg);
        setPopupErrorMessage("");
        try {
            await runGenerateSteps({ applyToPubmed: true });
        } finally {
            setIsGenerating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    // Regenerate steps for the active database on tab switch (PubMed's editable steps stay untouched)

    /* Builds the /generate-steps-embasecom request body — separate endpoint from PubMed's,
       reuses the same concept_groups/outcome_types/filters/publication_date shape but swaps
       in the Embase.com-specific Study Design query, using its own edit-override state
       (embaseComStudyDesignQueryOverride/Edited) — same pattern as PubMed's box, kept
       entirely independent so an edit in one never leaks into the other. */
    const buildGenerateStepsEmbaseComPayload = () => {
        const base = buildGenerateStepsPayload();
        if (!base) return null;
        const studyDesignKeys = selectedStudyDesigns.map((l) => optionKey(STUDY_DESIGN_OPTIONS, l)).filter(Boolean);
        const studyDesignQuery = studyDesignKeys.length
            ? (embaseComStudyDesignQueryEdited ? embaseComStudyDesignQueryOverride : buildEmbaseComStudyDesignQuery(studyDesignKeys))
            : "";
        return { ...base, study_design_query: studyDesignQuery };
    };

    /* Shared call to /generate-steps-embasecom. Only populates dbSteps.EMBASE_EMBASECOM —
       does not touch PubMed's editable steps or state, same isolation as the backend's
       separate service method/prompt. */
    const runGenerateStepsEmbaseCom = async () => {
        const body = buildGenerateStepsEmbaseComPayload();
        if (!body) {
            setPopupErrorMessage("At least Population is required to build search strings");
            return false;
        }
        try {
            const res = await axios.post(
                `${API_BASE_URL}/generate-steps-embasecom`,
                { ...body, research_question: researchQuestion.trim() || null },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return false;
            }

            if (res.data?.status === "success") {
                const data = res.data.data || {};
                const databases = data.databases || {};
                const embaseComPayload = databases.EMBASE_EMBASECOM;
                if (embaseComPayload) {
                    setDbSteps((prev) => ({ ...prev, EMBASE_EMBASECOM: embaseComPayload }));
                }
                return true;
            }

            setPopupErrorMessage(res.data?.message || "Failed to generate Embase.com search strings");
            return false;
        } catch (error) {
            setPopupErrorMessage(error.response?.data?.message || error.message || "Failed to generate Embase.com search strings");
            return false;
        }
    };

    /* Builds the /generate-steps-ovid request body — separate endpoint again,
       same shape but swaps in the Ovid-specific Study Design query, using its
       own edit-override state (ovidStudyDesignQueryOverride/Edited). */
    const buildGenerateStepsOvidPayload = () => {
        const base = buildGenerateStepsPayload();
        if (!base) return null;
        const studyDesignKeys = selectedStudyDesigns.map((l) => optionKey(STUDY_DESIGN_OPTIONS, l)).filter(Boolean);
        const studyDesignQuery = studyDesignKeys.length
            ? (ovidStudyDesignQueryEdited ? ovidStudyDesignQueryOverride : buildOvidStudyDesignQuery(studyDesignKeys))
            : "";
        return { ...base, study_design_query: studyDesignQuery };
    };

    /* Shared call to /generate-steps-ovid. Only populates dbSteps.EMBASE_OVID —
       does not touch PubMed's or Embase.com's state, same isolation as the
       backend's separate service method/prompt. */
    const runGenerateStepsOvid = async () => {
        const body = buildGenerateStepsOvidPayload();
        if (!body) {
            setPopupErrorMessage("At least Population is required to build search strings");
            return false;
        }
        try {
            const res = await axios.post(
                `${API_BASE_URL}/generate-steps-ovid`,
                { ...body, research_question: researchQuestion.trim() || null },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return false;
            }

            if (res.data?.status === "success") {
                const data = res.data.data || {};
                const databases = data.databases || {};
                const ovidPayload = databases.EMBASE_OVID;
                if (ovidPayload) {
                    setDbSteps((prev) => ({ ...prev, EMBASE_OVID: ovidPayload }));
                }
                return true;
            }

            setPopupErrorMessage(res.data?.message || "Failed to generate Embase (Ovid) search strings");
            return false;
        } catch (error) {
            setPopupErrorMessage(error.response?.data?.message || error.message || "Failed to generate Embase (Ovid) search strings");
            return false;
        }
    };

    /* Builds the /generate-steps-cochrane request body. Deliberately does NOT
       include study_design_query — Cochrane has no deterministic Study Design
       filter at all (every CENTRAL record is already a controlled trial), so
       there's nothing to swap in here, unlike the other three platforms. */
    const buildGenerateStepsCochranePayload = () => {
        const base = buildGenerateStepsPayload();
        if (!base) return null;
        const { study_design_query, ...rest } = base;
        return rest;
    };

    /* Shared call to /generate-steps-cochrane. Only populates dbSteps.COCHRANE —
       does not touch any other platform's state. */
    const runGenerateStepsCochrane = async () => {
        const body = buildGenerateStepsCochranePayload();
        if (!body) {
            setPopupErrorMessage("At least Population is required to build search strings");
            return false;
        }
        try {
            const res = await axios.post(
                `${API_BASE_URL}/generate-steps-cochrane`,
                { ...body, research_question: researchQuestion.trim() || null },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return false;
            }

            if (res.data?.status === "success") {
                const data = res.data.data || {};
                const databases = data.databases || {};
                const cochranePayload = databases.COCHRANE;
                if (cochranePayload) {
                    setDbSteps((prev) => ({ ...prev, COCHRANE: cochranePayload }));
                }
                return true;
            }

            setPopupErrorMessage(res.data?.message || "Failed to generate Cochrane search strings");
            return false;
        } catch (error) {
            setPopupErrorMessage(error.response?.data?.message || error.message || "Failed to generate Cochrane search strings");
            return false;
        }
    };

    const handleSwitchDatabase = async (dbKey) => {
        setActiveDatabase(dbKey);
        // Both Embase sub-platforms now have a backend generator, so we no
        // longer force activeEmbasePlatform to Embase.com on every click —
        // whichever sub-tab the user last picked persists across switches.
        if (dbKey === "PUBMED") return;
        if (inputMethod === "paste_query") return;
        if (!population.trim() || isRegenerating || isGenerating) return;
        // Saved-steps restoration (edit mode) is still in flight — its
        // additional_search_strategies payload may still populate dbSteps
        // for this exact platform any moment now. Generating here would
        // race it and fire a redundant AI call while data is already on
        // its way in.
        if (isRestoringSavedSteps) return;

        // Tab switching itself is always allowed (handled above); only the
        // actual generation call below is gated on Review Type consistency.
        const guardMsg = getReviewTypeGuardMessage();
        if (guardMsg) {
            setPopupErrorMessage(guardMsg);
            return;
        }

        if (dbKey === "EMBASE") {
            const platformKey = activeEmbasePlatform === "EMBASE_OVID" ? "EMBASE_OVID" : "EMBASE_EMBASECOM";
            // Tab switching only fills an EMPTY tab — it never silently
            // regenerates content that's already there (restored from a
            // save, or generated earlier this session), even if PICOS has
            // since changed. A mismatch is surfaced as a warning near the
            // Rebuild button, same as PubMed's queryOutOfDate; only an
            // explicit Rebuild click re-runs the AI for stale content.
            const alreadyGenerated = dbSteps[platformKey]?.steps?.length > 0;
            if (alreadyGenerated) return;
            if (generationInFlightRef.current) return;
            generationInFlightRef.current = true;

            setIsRegenerating(true);
            setIsProcessingPopupVisible(true);
            setProcessingMessage(
                platformKey === "EMBASE_OVID"
                    ? "Generating Embase (Ovid) search steps with AI..."
                    : "Generating Embase.com search steps with AI..."
            );
            try {
                if (platformKey === "EMBASE_OVID") {
                    await runGenerateStepsOvid();
                } else {
                    await runGenerateStepsEmbaseCom();
                }
            } finally {
                generationInFlightRef.current = false;
                setIsRegenerating(false);
                setIsProcessingPopupVisible(false);
                setProcessingMessage("");
            }
            return;
        }

        if (dbKey === "COCHRANE") {
            // Same rule as EMBASE above — tab switch only fills an empty
            // tab, never silently re-triggers AI on top of existing content.
            const alreadyGenerated = dbSteps["COCHRANE"]?.steps?.length > 0;
            if (alreadyGenerated) return;
            if (generationInFlightRef.current) return;
            generationInFlightRef.current = true;

            setIsRegenerating(true);
            setIsProcessingPopupVisible(true);
            setProcessingMessage("Generating Cochrane search steps with AI...");
            try {
                await runGenerateStepsCochrane();
            } finally {
                generationInFlightRef.current = false;
                setIsRegenerating(false);
                setIsProcessingPopupVisible(false);
                setProcessingMessage("");
            }
            return;
        }

        // Tab switching only fills an empty tab — same rule as EMBASE/COCHRANE above.
        const alreadyGenerated = dbSteps[dbKey]?.steps?.length > 0 || dbSteps[dbKey]?.final_query;
        if (alreadyGenerated) return;
        if (generationInFlightRef.current) return;
        generationInFlightRef.current = true;

        setIsRegenerating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Generating steps for ${DB_LABELS[dbKey] || dbKey}...`);
        try {
            await runGenerateSteps({ applyToPubmed: false });
        } finally {
            generationInFlightRef.current = false;
            setIsRegenerating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* Unified Rebuild handler — the Rebuild button now shows on every DB tab.
       Routes to whichever backend call matches the active tab, showing the
       same processing popup for all of them. All four platforms now have a
       real backend generator. */
    const handleRebuildActiveTab = async () => {
        if (isProcessing || isRegenerating) return;
        if (isRestoringSavedSteps) return;
        if (generationInFlightRef.current) return;

        const guardMsg = getReviewTypeGuardMessage();
        if (guardMsg) {
            setPopupErrorMessage(guardMsg);
            return;
        }

        if (activeDatabase === "PUBMED") {
            generationInFlightRef.current = true;
            try {
                await handleBuildStrings();
            } finally {
                generationInFlightRef.current = false;
            }
            return;
        }

        generationInFlightRef.current = true;
        try {
            if (activeDatabase === "COCHRANE") {
                setIsGenerating(true);
                setIsProcessingPopupVisible(true);
                setProcessingMessage("Generating Cochrane search steps with AI...");
                setPopupErrorMessage("");
                try {
                    await runGenerateStepsCochrane();
                } finally {
                    setIsGenerating(false);
                    setIsProcessingPopupVisible(false);
                    setProcessingMessage("");
                }
                return;
            }

            if (activeDatabase === "EMBASE") {
                const platformKey = activeEmbasePlatform === "EMBASE_OVID" ? "EMBASE_OVID" : "EMBASE_EMBASECOM";
                setIsGenerating(true);
                setIsProcessingPopupVisible(true);
                setProcessingMessage(
                    platformKey === "EMBASE_OVID"
                        ? "Generating Embase (Ovid) search steps with AI..."
                        : "Generating Embase.com search steps with AI..."
                );
                setPopupErrorMessage("");
                try {
                    if (platformKey === "EMBASE_OVID") {
                        await runGenerateStepsOvid();
                    } else {
                        await runGenerateStepsEmbaseCom();
                    }
                } finally {
                    setIsGenerating(false);
                    setIsProcessingPopupVisible(false);
                    setProcessingMessage("");
                }
            }
            // All four platforms (PubMed, Cochrane, Embase.com, Embase Ovid) are
            // handled above — nothing falls through to here anymore.
        } finally {
            generationInFlightRef.current = false;
        }
    };

    // 3. ENDPOINT: /search-form/save
    const handleCreateSearch = async () => {
        const isPasteMode = inputMethod === "paste_query";
        const activeQuery = isPasteMode ? pastedQuery.trim() : generatedQuery.trim();

        if (isCreating) return;

        const guardMsg = getReviewTypeGuardMessage();
        if (guardMsg) {
            setPopupErrorMessage(guardMsg);
            return;
        }

        if (!searchName.trim()) {
            setPopupErrorMessage("Name is required to save this search.");
            return;
        }

        if (!activeQuery) return;

        if (editSearch?.id && !isPasteMode && activeSteps.length === 0) {
            setPopupErrorMessage(
                "Saved steps haven't loaded yet. Wait a moment or rebuild the search strings before saving."
            );
            return;
        }

        setIsCreating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Saving search form...");
        setPopupErrorMessage("");

        // Build the answers blob — the full form state stored verbatim in discovery.form_answers
        const answers = {
            REVIEW_TYPE: reviewTypes,
            POPULATION: population.trim() || "",
            INTERVENTION: intervention.trim() || "",
            COMPARATOR: comparator.trim() || "",
            OUTCOMES: outcomes.trim() || "",
            STUDY_DESIGN: studyDesignText.trim() || "",
            OTHER_OUTCOMES: otherOutcomes.trim() || "",
            OUTCOME_TYPES: selectedOutcomeTypes.flatMap((o) => {
                if (o === "Other") {
                    const txt = otherOutcomeTypeText.trim();
                    return txt ? [txt.replace(/\s+/g, "_").toUpperCase()] : [];
                }
                return [o.replace(/\s+/g, "_").toUpperCase()];
            }),
            filters: {
                text_availability: selectedTextAvailability.map((t) => t.replace(/\s+/g, "_").toUpperCase()),
                study_design: selectedStudyDesigns.map((l) => optionKey(STUDY_DESIGN_OPTIONS, l)).filter(Boolean),
                publication_type: selectedPubTypes.map((l) => optionKey(PUBLICATION_TYPE_OPTIONS, l)).filter(Boolean),
                species: selectedSpecies.map((l) => optionKey(SPECIES_OPTIONS, l)).filter(Boolean),
                sex: selectedSex.map((l) => optionKey(SEX_OPTIONS, l)).filter(Boolean),
                language: selectedLanguages.map((l) => optionKey(LANGUAGE_OPTIONS, l)).filter(Boolean),
                has_associated_data: hasAssociatedData,
                geography: [
                    ...[...selectedCountries, ...selectedRegions].map((l) => optionKey(GEO_OPTIONS, l)).filter(Boolean),
                    ...customGeoEntries,
                ],
            },
            publication_date: {
                preset: dateFilter === "Custom range" ? "CUSTOM" : (dateFilter ? dateFilter.replace(/\s+/g, "_").toUpperCase() : "NO_RESTRICTION"),
                from: dateFilter === "Custom range" ? customDateFrom || null : null,
                to: dateFilter === "Custom range" ? customDateTo || null : null,
            },
            include_flags: {
                outcomes: includeOutcomesInSearch,
                publication_type: includePubTypesInSearch,
                intervention: includeInterventionInSearch,
            },
            active_database: activeDatabase,
        };

        // Build steps array matching SearchStepModel schema
        let stepsPayload;
        if (isPasteMode) {
            // Custom Query mode: single step with the pasted query
            stepsPayload = [{
                step_number: 1,
                description: "Custom query",
                query_string: pastedQuery.trim(),
                step_role: "FINAL",
                origin_key: null,
                is_final: true,
                source: "MANUAL",
                is_edited: false,
            }];
        } else {
            // Research Question / PICOS mode: use AI-generated steps only.
            const savedSteps = customSteps
                .filter((s) => !removedStepIds.includes(s.id))
                .map((s) => (stepEdits[s.id] ? { ...s, ...stepEdits[s.id] } : s));

            stepsPayload = savedSteps.map((step, idx) => ({
                step_number: idx + 1,
                description: step.description || `Step ${idx + 1}`,
                query_string: step.isFinal ? derivedFinalQuery : (step.query || ""),
                step_role: step.stepRole || "CONCEPT",
                origin_key: step.originKey || null,
                is_final: step.isFinal || false,
                source: step.auto ? "AI" : "MANUAL",
                is_edited: Boolean(stepEdits[step.id]),
            }));

            // Ensure exactly one FINAL step exists
            const hasFinal = stepsPayload.some((s) => s.is_final);
            if (!hasFinal && stepsPayload.length > 0) {
                stepsPayload.push({
                    step_number: stepsPayload.length + 1,
                    description: `Combine ${stepsPayload.length} step${stepsPayload.length > 1 ? "s" : ""}`,
                    query_string: generatedQuery,
                    step_role: "FINAL",
                    origin_key: null,
                    is_final: true,
                    source: "AI",
                    is_edited: false,
                });
            }
        }

        const inputMethodMap = {
            research_question: "RESEARCH_QUESTION",
            manual_picos: "MANUAL",
            paste_query: "PASTE_QUERY",
        };

        // dbSteps holds the OTHER platforms' already-generated strategies
        // (EMBASE_OVID / EMBASE_EMBASECOM / COCHRANE) — persist them as an
        // opaque snapshot so reopening this discovery restores them instead
        // of forcing a fresh AI regeneration on every tab. PUBMED is excluded
        // since it's already the primary saved strategy (steps/search_query
        // above) and shouldn't be duplicated here.
        const { PUBMED: _pubmedDbSteps, ...otherDbStepsForSave } = dbSteps;

        const payload = {
            discovery_id: editSearch?.id || null,
            name: searchName.trim(),
            framework: isPasteMode ? null : "PICOS",
            input_method: inputMethodMap[inputMethod] || "RESEARCH_QUESTION",
            research_question: researchQuestion.trim() || null,
            pasted_query: isPasteMode ? pastedQuery.trim() : null,
            answers,
            steps: stepsPayload,
            source_type: activeDatabase === "PUBMED" ? "PUBMED" : activeDatabase,
            slr_fabric_id: slrFabricId || null,
            additional_databases: Object.keys(otherDbStepsForSave).length ? otherDbStepsForSave : null,
        };

        try {
            const res = await axios.post(`${API_BASE_URL}/search-form/save`, payload, { headers });

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage(isEditMode
                    ? "Literature search updated successfully"
                    : "Literature search saved successfully");
                const data = res.data.data || {};
                // Build a createdSearch object compatible with setActiveSearch dispatch
                const createdSearch = {
                    id: data.discovery_id,
                    query: data.search_query || activeQuery,
                    name: searchName.trim(),
                    search_query: data.search_query || activeQuery,
                    source_type: activeDatabase === "PUBMED" ? "PUBMED" : activeDatabase,
                    total_results: null,
                    step_count: data.total_steps || stepsPayload.length,
                    status: "CREATED",
                    hits_count: data.total_steps || null,
                    input_method: inputMethodMap[inputMethod] || "RESEARCH_QUESTION",
                    research_question: researchQuestion.trim() || null,
                    form_answers: answers,
                };
                setTimeout(() => { onSuccess?.(createdSearch); }, 1000);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to save search form");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Something went wrong while saving search");
        } finally {
            setIsCreating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ── Client-side export — no backend call, builds a .txt from already-generated steps ── */
    const slugify = (str) => {
        const s = (str || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
        return s || "Literature_Search";
    };
    const dateStamp = () => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    };

    /* Steps to export for a given DB key — PubMed uses the live/edited step list, others use the AI-generated payload as-is */
    const buildExportLines = (dbKey) => {
        if (inputMethod === "paste_query") {
            return pastedQuery.trim()
                ? [{ step_number: 1, description: "Query", query_string: pastedQuery.trim(), is_final: true }]
                : [];
        }
        if (dbKey === "PUBMED") {
            return activeSteps.map((s, idx) => ({
                step_number: idx + 1,
                description: s.description || `Step ${idx + 1}`,
                query_string: s.query || "",
                is_final: !!s.isFinal,
            }));
        }
        const edits = otherDbStepEdits[dbKey] || {};
        return (dbSteps[dbKey]?.steps || []).map((s) => {
            const patch = edits[s.step_number];
            return {
                step_number: s.step_number,
                description: patch?.description ?? s.description,
                query_string: patch?.query_string ?? s.query_string,
                is_final: s.is_final,
            };
        });
    };

    /* Remaps #N references in a non-PubMed final query after local deletions — same idea as
       derivedFinalQuery above, generalized to take any db's raw steps + its removed step numbers. */
    const remapFinalQueryRefs = (baseQuery, allSteps, removedStepNumbers) => {
        if (!baseQuery || !removedStepNumbers || removedStepNumbers.length === 0) return baseQuery;

        const nonFinal = allSteps.filter((s) => !s.is_final);
        const activeNonFinal = nonFinal.filter((s) => !removedStepNumbers.includes(s.step_number));

        // Build remap: original step_number → new 1-based position (null = deleted)
        const remap = {};
        nonFinal.forEach((s) => {
            const newIdx = activeNonFinal.findIndex((a) => a.step_number === s.step_number);
            remap[s.step_number] = newIdx >= 0 ? newIdx + 1 : null;
        });

        return remapQueryRefs(baseQuery, remap);
    };
    /* On-screen final step display — the reference-style query (e.g. "#1 AND #2 AND ...") that lives
       on the final step itself, remapped for local deletions. This is NOT the same as final_query
       (which is fully expanded/substituted and used only for export). Mirrors how PubMed's
       derivedFinalQuery reads from the final step's own `query`, not from generatedQuery. */
    const getDisplayFinalQuery = (dbKey) => {
        const rawSteps = dbSteps[dbKey]?.steps || [];
        const finalStepRaw = rawSteps.find((s) => s.is_final);
        if (!finalStepRaw) return "";

        const edited = otherDbStepEdits[dbKey]?.[finalStepRaw.step_number];
        if (edited?.query_string !== undefined) return edited.query_string;

        const removedStepNumbers = otherDbRemovedSteps[dbKey] || [];
        return remapFinalQueryRefs(finalStepRaw.query_string || "", rawSteps, removedStepNumbers);
    };

    /* Fully expanded, ready-to-paste final query (backend's final_query — #N references already
       substituted). Distinct from the step-reference query_string ("#4 AND (#5 OR ...)") already
       present in the step rows. Returns "" for paste_query mode — that case has no separate final
       query, the pasted query itself IS the row. */
    const getFinalQueryForDb = (dbKey) => {
        if (inputMethod === "paste_query") return { text: "", warning: "" };
        if (dbKey === "PUBMED") {
            // derivedFinalQuery is for the ON-SCREEN editable Step-6 formula
            // display and deliberately keeps #N references intact when no
            // steps have been removed. Export needs the flattened,
            // ready-to-paste text instead — that's generatedQuery, which is
            // set directly from the backend's already-expanded final_query
            // (see setGeneratedQuery(pubmedPayload.final_query...) on build).
            // A manual edit to the final step still takes precedence, same
            // as every other platform's branch below.
            const finalStep = activeSteps.find((s) => s.isFinal);
            if (finalStep?.id && stepEdits[finalStep.id]?.query !== undefined) {
                return { text: stepEdits[finalStep.id].query, warning: "" };
            }
            return {
                text: generatedQuery || "",
                warning: queryOutOfDate
                    ? "PICOS changed since this was built — rebuild recommended for an accurate query."
                    : "",
            };
        }

        const rawSteps = dbSteps[dbKey]?.steps || [];
        const finalStepRaw = rawSteps.find((s) => s.is_final);

        // If the user directly edited the final step's own query, that takes precedence —
        // skip remap entirely, same as derivedFinalQuery does for PubMed.
        const editedFinal = finalStepRaw ? otherDbStepEdits[dbKey]?.[finalStepRaw.step_number] : null;
        if (editedFinal?.query_string !== undefined) {
            return { text: editedFinal.query_string, warning: "" };
        }

        const hasNonFinalEdits = Object.keys(otherDbStepEdits[dbKey] || {})
            .some((num) => Number(num) !== finalStepRaw?.step_number);
        const removedStepNumbers = otherDbRemovedSteps[dbKey] || [];
        const remappedQuery = remapFinalQueryRefs(dbSteps[dbKey]?.final_query || "", rawSteps, removedStepNumbers);

        return {
            text: remappedQuery,
            warning: hasNonFinalEdits
                ? "Steps were edited after this combined query was generated — copy from the individual step rows, or rebuild, for an accurate final string."
                : "",
        };
    };

    /* sheets: [{ name: "PubMed", rows: [{step_number, description, query_string, is_final}, ...], finalQuery, finalQueryWarning }, ...] */
    const downloadExcelFile = (filename, sheets) => {
        const wb = XLSX.utils.book_new();
        sheets.forEach(({ name, rows, finalQuery, finalQueryWarning }) => {
            const aoa = [
                ["Step", "Description", "Query", "Final"],
                ...(rows.length
                    ? rows.map((s) => [s.step_number, s.description || "", s.query_string || "", s.is_final ? "Yes" : ""])
                    : [["", "(no steps)", "", ""]]),
            ];
            if (finalQuery) {
                aoa.push(["", "", "", ""]);
                aoa.push(["FINAL QUERY (ready to paste)", finalQuery, "", ""]);
                if (finalQueryWarning) {
                    aoa.push(["⚠ Note", finalQueryWarning, "", ""]);
                }
            }
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            /* Excel sheet names: max 31 chars, no []:*?/\\ */
            const safeName = (name || "Sheet").replace(/[\[\]:*?/\\]/g, "").slice(0, 31) || "Sheet";
            XLSX.utils.book_append_sheet(wb, ws, safeName);
        });
        XLSX.writeFile(wb, filename);
    };

    /* dbKey: "PUBMED" | "EMBASE" | "COCHRANE" | "ALL" */
    const handleExportSteps = (dbKey) => {
        setExportingDb(dbKey);
        try {
            const baseName = slugify(
                inputMethod === "paste_query" ? "Pasted_Query" : (researchQuestion || population)
            );
            const stamp = dateStamp();

            if (dbKey === "ALL") {
                const keys = inputMethod === "paste_query"
                    ? ["PUBMED", "EMBASE_OVID", "EMBASE_EMBASECOM", "COCHRANE"]
                    : (Object.keys(dbSteps).length ? Object.keys(dbSteps) : ["PUBMED"]);
                const sheets = keys.map((k) => {
                    const { text, warning } = getFinalQueryForDb(k);
                    return { name: DB_LABELS[k] || k, rows: buildExportLines(k), finalQuery: text, finalQueryWarning: warning };
                });
                downloadExcelFile(`${baseName}_All_Databases_${stamp}.xlsx`, sheets);
            } else if (dbKey === "EMBASE") {
                // Export both Embase platforms as separate sheets
                const sheets = EMBASE_PLATFORMS
                    .filter((p) => dbSteps[p.key]?.steps?.length > 0)
                    .map((p) => {
                        const { text, warning } = getFinalQueryForDb(p.key);
                        return { name: p.label, rows: buildExportLines(p.key), finalQuery: text, finalQueryWarning: warning };
                    });
                if (!sheets.length) {
                    setPopupErrorMessage("Nothing to export yet for Embase.");
                    return;
                }
                downloadExcelFile(`${baseName}_Embase_${stamp}.xlsx`, sheets);
            } else {
                const lines = buildExportLines(dbKey);
                if (!lines.length) {
                    setPopupErrorMessage("Nothing to export yet for this database.");
                    return;
                }
                const { text, warning } = getFinalQueryForDb(dbKey);
                downloadExcelFile(`${baseName}_${DB_LABELS[dbKey] || dbKey}_${stamp}.xlsx`, [
                    { name: DB_LABELS[dbKey] || dbKey, rows: lines, finalQuery: text, finalQueryWarning: warning },
                ]);
            }
        } catch (err) {
            setPopupErrorMessage("Failed to export steps");
        } finally {
            setExportingDb(null);
            setExportMenuOpen(false);
        }
    };

    // Close the export dropdown on outside click
    useEffect(() => {
        if (!exportMenuOpen) return;
        const handler = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setExportMenuOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [exportMenuOpen]);

    const fileInputRef = useRef(null);
    const justImportedRef = useRef(false); // set true on import confirm; useEffect captures fingerprint as new baseline

    /* Single "Export" button (kept as it was) — click reveals two choices: only the active DB, or all. */
    const renderExportButton = (disabled) => {
        const activeDbHasContent = buildExportLines(activeDatabase).length > 0;
        return (
            <div className="position-relative" ref={exportMenuRef}>
                <button type="button" className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
                    onClick={() => setExportMenuOpen((p) => !p)}
                    disabled={disabled || !!exportingDb}>
                    {exportingDb
                        ? <><span className="spinner-border spinner-border-sm me-1"></span> Exporting...</>
                        : <><i className="bi bi-download me-1"></i> Export to Excel</>}
                </button>
                {exportMenuOpen && (
                    <div className="ev-card-4 position-absolute top-100 start-0 mt-1 p-1 ev-radius-xsmall shadow-sm z-3" style={{ minWidth: 180 }}>
                        <button type="button"
                            className="ev-btn-light-fill ev-btn-small ev-radius-xsmall w-100 text-start mb-1"
                            onClick={() => handleExportSteps(activeDatabase)}
                            disabled={!activeDbHasContent}>
                            Only {DB_LABELS[activeDatabase] || activeDatabase}
                        </button>
                        <button type="button"
                            className="ev-btn-light-fill ev-btn-small ev-radius-xsmall w-100 text-start"
                            onClick={() => handleExportSteps("ALL")}
                            disabled={!isBuilt}>
                            All Databases
                        </button>
                    </div>
                )}
            </div>
        );
    };
    /* ── CSV field parser — handles quoted fields and escaped double-quotes ── */
    const parseCSVLine = (line) => {
        const cols = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
                else { inQuotes = !inQuotes; }
            } else if (ch === "," && !inQuotes) {
                cols.push(current.trim());
                current = "";
            } else {
                current += ch;
            }
        }
        cols.push(current.trim());
        return cols;
    };

    /* PubMed query patterns: field tags like [MeSH],[tiab] or boolean AND/OR/NOT */
    const PUBMED_FIELD_TAG_RE = /\[[a-zA-Z]{2,10}\]/;
    const PUBMED_BOOL_RE = /\b(AND|OR|NOT)\b/;
    const isPubMedQuery = (q) => PUBMED_FIELD_TAG_RE.test(q) || PUBMED_BOOL_RE.test(q);

    /* Upload → validate immediately → error popup on any failure, modal on pass */
    const handleImportExcel = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";

        const name = file.name.toLowerCase();
        const isXlsx = name.endsWith(".xlsx");
        const isCsv = name.endsWith(".csv");

        if (!isXlsx && !isCsv) {
            setPopupErrorMessage("Only .csv or .xlsx files are supported.");
            return;
        }

        const processRows = (rawMatrix) => {
            // rawMatrix: array of arrays (first row = header)
            if (rawMatrix.length < 2) {
                setPopupErrorMessage("File appears to be empty or contains no data rows.");
                return;
            }
            const rows = [];
            rawMatrix.forEach((cols, i) => {
                if (i === 0) return;
                const step = String(cols[0] ?? "");
                const desc = String(cols[1] ?? "");
                const query = String(cols[2] ?? "");
                const isFinal = String(cols[3] ?? "").trim().toLowerCase() === "yes";
                if (!query || query.toLowerCase().startsWith("final query")) return;
                rows.push({ step: step || String(i), description: desc || `Step ${i}`, query, isFinal });
            });

            if (rows.length === 0) {
                setPopupErrorMessage("No data rows found. Expected columns: Step, Description, Query.");
                return;
            }

            // If no step is marked Final, auto-mark the last one
            if (!rows.some((r) => r.isFinal)) {
                rows[rows.length - 1].isFinal = true;
            }

            const failingRow = rows.find((r) => !isPubMedQuery(r.query));
            if (failingRow) {
                setPopupErrorMessage(
                    `"${failingRow.description}" does not contain a valid PubMed query. ` +
                    `All steps must include PubMed field tags (e.g. [MeSH], [tiab]) or boolean operators (AND / OR / NOT).`
                );
                return;
            }

            const ts = Date.now();
            setPendingImport(rows.map((r, i) => ({ id: `import-${ts}-${i}`, description: r.description, query: r.query, isFinal: r.isFinal, auto: false })));
        };

        if (isXlsx) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const wb = XLSX.read(new Uint8Array(evt.target.result), { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
                processRows(matrix);
            };
            reader.readAsArrayBuffer(file);
        } else {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const lines = (evt.target.result || "").split(/\r?\n/).filter((l) => l.trim());
                const matrix = lines.map(parseCSVLine);
                processRows(matrix);
            };
            reader.readAsText(file);
        }
    };

    /* Commit validated steps to customSteps */
    /* Strip PubMed syntax from a query string to get readable terms for form population */
    const extractTermsFromImportedQuery = (query) => {
        return query
            .replace(/#\d+/g, "")                    // remove #N step references
            .replace(/\[[^\]]+\]/g, "")              // remove [field tags]
            .replace(/\b(AND|OR|NOT)\b/g, " ")       // remove boolean operators
            .replace(/[()"""]/g, " ")                // remove parens and quotes
            .replace(/\s+/g, " ")
            .trim();
    };

    /* Scan all imported step queries and populate filter state from detected PubMed syntax */
    const detectAndSetFiltersFromImport = (steps) => {
        const fullText = steps.map((s) => s.query || "").join(" ");

        // Publication Type — "term"[pt]
        const pubTypes = [];
        if (/"meta.?analysis"\[pt\]/i.test(fullText)) pubTypes.push("Meta-Analysis");
        if (/"systematic review"\[pt\]/i.test(fullText)) pubTypes.push("Systematic Review");
        if (/"journal article"\[pt\]/i.test(fullText)) pubTypes.push("Journal Article");
        if (/"case reports?"\[pt\]/i.test(fullText)) pubTypes.push("Case Report");
        if (/"(?:practice )?guideline"\[pt\]/i.test(fullText)) pubTypes.push("Guidelines");
        if (/"congress"\[pt\]/i.test(fullText)) pubTypes.push("Conference Abstracts");
        if (pubTypes.length) setSelectedPubTypes(pubTypes);

        // Language — "term"[lang] or "term"[la]
        const langs = [];
        if (/"english"\[(?:lang|la)\]/i.test(fullText)) langs.push("English");
        if (/"spanish"\[(?:lang|la)\]/i.test(fullText)) langs.push("Spanish");
        if (/"french"\[(?:lang|la)\]/i.test(fullText)) langs.push("French");
        if (/"german"\[(?:lang|la)\]/i.test(fullText)) langs.push("German");
        if (langs.length) setSelectedLanguages(langs);

        // Species — "term"[MeSH] or "term"[mh]
        const sp = [];
        if (/"humans?"\[(?:mesh|mh)\]/i.test(fullText)) sp.push("Humans");
        if (/"animals?"\[(?:mesh|mh)\]/i.test(fullText)) sp.push("Other Animals");
        if (sp.length) setSelectedSpecies(sp);

        // Sex — "term"[MeSH]
        const sex = [];
        if (/"female"\[(?:mesh|mh)\]/i.test(fullText)) sex.push("Female");
        if (/"male"\[(?:mesh|mh)\]/i.test(fullText)) sex.push("Male");
        if (sex.length) setSelectedSex(sex);

        // Study Design — [pt] or [MeSH]
        const studyDesigns = [];
        if (/"(?:randomized|randomised) controlled trial"\[(?:pt|mesh|mh)\]/i.test(fullText)) studyDesigns.push(optionLabel(STUDY_DESIGN_OPTIONS, "RCT"));
        if (/"clinical trial"\[(?:pt|mesh|mh)\]/i.test(fullText)) studyDesigns.push(optionLabel(STUDY_DESIGN_OPTIONS, "CLINICAL_TRIAL"));
        if (/"pragmatic"\[(?:tiab|mesh|mh)\]/i.test(fullText)) studyDesigns.push(optionLabel(STUDY_DESIGN_OPTIONS, "PRAGMATIC_TRIALS"));
        if (/"cohort studi|"observational stud|real.world\b/i.test(fullText)) studyDesigns.push(optionLabel(STUDY_DESIGN_OPTIONS, "OBSERVATIONAL_RWE"));
        if (studyDesigns.length) setSelectedStudyDesigns([...new Set(studyDesigns.filter(Boolean))]);

        // Text Availability
        const textAvail = [];
        if (/hasabstract/i.test(fullText)) textAvail.push("Has Abstract");
        if (/free\s+full\s+text/i.test(fullText)) textAvail.push("Free Full Text");
        else if (/full\s+text/i.test(fullText)) textAvail.push("Full Text");
        if (textAvail.length) setSelectedTextAvailability(textAvail);

        // Geography — match known country and region names from GEO_OPTIONS against [MeSH] or [tiab]
        const geoLabels = [];
        GEO_OPTIONS.forEach(({ label }) => {
            const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            if (new RegExp(`"${escaped}"\\[(?:mesh|mh|tiab|ti)\\]`, "i").test(fullText)) {
                geoLabels.push(label);
            }
        });
        const countries = geoLabels.filter((l) => GEO_COUNTRIES.includes(l));
        const regions = geoLabels.filter((l) => GEO_REGIONS.includes(l));
        if (countries.length) setSelectedCountries(countries);
        if (regions.length) setSelectedRegions(regions);
    };

    const handleConfirmImport = () => {
        if (!pendingImport?.length) return;
        const count = pendingImport.length;

        // Populate PICOS form fields by matching step descriptions to known labels
        const nonFinalSteps = pendingImport.filter((s) => !s.isFinal);
        nonFinalSteps.forEach((step) => {
            const d = step.description.toLowerCase().trim();
            const terms = extractTermsFromImportedQuery(step.query);
            if (!terms) return;
            if (["population", "pop", "patient", "participants", "subject"].some((k) => d.includes(k))) {
                setPopulation(terms);
            } else if (["intervention", "exposure", "treatment", "drug"].some((k) => d.includes(k)) && !d.includes("comparator")) {
                setIntervention(terms);
            } else if (["comparator", "control", "comparison"].some((k) => d.includes(k))) {
                setComparator(terms);
            } else if (["outcome", "result", "endpoint"].some((k) => d.includes(k))) {
                setOutcomes(terms);
            }
        });

        setCustomSteps(pendingImport);
        setRemovedStepIds([]);
        setStepEdits({});
        setGeneratedQuery("");
        detectAndSetFiltersFromImport(pendingImport);
        setIsBuilt(true);
        setPendingImport(null);
        justImportedRef.current = true; // useEffect will capture new fingerprint as baseline once state settles
        setPopupSuccessMessage(`${count} step(s) imported successfully`);
    };

    /* Cancel — clear pending import state */
    const handleCancelImport = () => {
        setPendingImport(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    /* ── Sample CSV download — shows users the expected format ── */
    const handleDownloadSampleCsv = () => {
        const sampleRows = [
            ["Step", "Description", "Query", "Final"],
            [1, "Population", '("diabetes mellitus"[MeSH] OR "diabetes"[tiab] OR "T2DM"[tiab])', ""],
            [2, "Intervention", '("semaglutide"[tiab] OR "liraglutide"[tiab] OR "GLP-1 agonist"[tiab])', ""],
            [3, "Outcome", '("HbA1c"[tiab] OR "glycemic control"[tiab] OR "cardiovascular outcome"[tiab])', ""],
            [4, "Final combined search", "(#1 AND #2 AND #3)", "Yes"],
        ];
        const csvContent = sampleRows.map((r) =>
            r.map((cell) => (String(cell).includes(",") || String(cell).includes('"') ? `"${String(cell).replace(/"/g, '""')}"` : cell)).join(",")
        ).join("\r\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Eavior_PubMed_Search_Sample.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const [queryAnalysis, setQueryAnalysis] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    const handleAnalyzeQuery = async () => {
        if (!pastedQuery.trim() || isAnalyzing) return;
        setIsAnalyzing(true);
        setQueryAnalysis(null);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/query-analysis`,
                { query_string: pastedQuery.trim(), include_estimate: true },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status === "success" && res.data.data) {
                const d = res.data.data;
                setQueryAnalysis({
                    valid: d.valid,
                    validationErrors: d.validation_errors || [],
                    meshCount: d.mesh_terms?.count || 0,
                    meshTerms: (d.mesh_terms?.terms || []).slice(0, 5).join(", "),
                    tiabCount: d.free_text_terms?.count || 0,
                    freeTextTerms: (d.free_text_terms?.terms || []).slice(0, 5).join(", "),
                    andCount: d.operators?.AND || 0,
                    orCount: d.operators?.OR || 0,
                    notCount: d.operators?.NOT || 0,
                    filtersDetected: [
                        ...(d.filters?.language || []).map((l) => `Lang: ${l}`),
                        d.filters?.date ? (d.filters.date.type === "LAST_N_YEARS" ? `Last ${d.filters.date.years}y` : "Date range") : null,
                        ...(d.filters?.publication_type || []).map((p) => p),
                    ].filter(Boolean).join(" · ") || "none",
                    estimatedRecords: d.estimated_records,
                    tips: d.tips || [],
                    suggestedFix: d.suggested_fix || null,
                    hasStudyDesign: !(d.tips || []).some((t) => t.field === "study_design"),
                });
            } else {
                setQueryAnalysis(buildLocalAnalysis(pastedQuery));
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            setQueryAnalysis(buildLocalAnalysis(pastedQuery));
        } finally { setIsAnalyzing(false); }
    };

    const buildLocalAnalysis = (q) => {
        if (!q || !q.trim()) return null;
        const meshCount = (q.match(/\[mesh\]/gi) || []).length;
        const tiabCount = (q.match(/\[tiab\]/gi) || []).length;
        const andCount = (q.match(/\bAND\b/g) || []).length;
        const orCount = (q.match(/\bOR\b/g) || []).length;
        const hasLang = /\[la\]/i.test(q);
        const hasDate = /\[dp\]/i.test(q);
        const hasStudyDesign = /(RCT|randomi[sz]ed|cohort|observational|clinical trial|meta-analysis|systematic review)/i.test(q);
        let depth = 0, balanced = true;
        for (const ch of q) { if (ch === "(") depth++; if (ch === ")") depth--; if (depth < 0) { balanced = false; break; } }
        if (depth !== 0) balanced = false;
        const estLow = meshCount * 200 + tiabCount * 90;
        const estHigh = estLow * 2 + 50;
        return {
            meshCount, tiabCount, andCount, orCount,
            filtersDetected: [hasLang && "Language", hasDate && "Date"].filter(Boolean).join(" · ") || "none",
            hasStudyDesign,
            valid: balanced && q.trim().length > 0,
            validationErrors: balanced ? [] : ["Unbalanced parentheses detected"],
            estimatedRecords: null,
            tips: [],
            suggestedFix: null,
            meshTerms: "",
            freeTextTerms: "",
            notCount: 0,
        };
    };

    const handleGenerateFromPrompt = async () => {
        if (!aiPrompt.trim() || isGenerating) return;
        setIsGenerating(true); setIsProcessingPopupVisible(true);
        setProcessingMessage("Generating query..."); setPopupErrorMessage("");
        try {
            const res = await axios.post(`${API_BASE_URL}/generate-query`, { natural_language_query: aiPrompt.trim() }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status === "success") {
                const q = res.data.data?.pubmed_query || "";
                setPastedQuery(q); setGeneratedQuery(q);
            } else { setPopupErrorMessage(res.data?.message || "Failed to generate query"); }
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            setPopupErrorMessage(error.response?.data?.message || error?.message || "Error generating query");
        } finally { setIsGenerating(false); setIsProcessingPopupVisible(false); setProcessingMessage(""); }
    };

    const canSave = searchName.trim().length > 0 && (inputMethod === "paste_query"
        ? pastedQuery.trim().length > 0
        : isBuilt && generatedQuery.trim().length > 0 && activeDatabase === "PUBMED");

    const renderFiltersPanel = () => (
        <>
            <SectionHeading number={inputMethod === "research_question" ? "5" : "4"} title="Filters" />

            <FilterGroup id="textAvailability" label="Text Availability" count={selectedTextAvailability.length} open={openFilters.textAvailability} onToggle={toggleFilterOpen}>
                <CheckboxGroup items={TEXT_AVAILABILITY_FILTERS} selected={selectedTextAvailability} onToggle={toggleItem(setSelectedTextAvailability)} columns={3} />
            </FilterGroup>

            <FilterGroup id="studyDesign" label="Study Design" count={selectedStudyDesigns.length} open={openFilters.studyDesign} onToggle={toggleFilterOpen} highlighted={recentlyFilled.studyDesign}>
                {(() => {
                    const visibleKeysForReview = reviewTypes.length
                        ? [...new Set(reviewTypes.flatMap((k) => REVIEW_TYPE_STUDY_DESIGN_VISIBLE[k] || []))]
                        : null;
                    const visibleStudyDesignFilters = visibleKeysForReview
                        ? STUDY_DESIGN_OPTIONS.filter((o) => visibleKeysForReview.includes(o.key)).map((o) => o.label)
                        : STUDY_DESIGN_FILTERS;
                    return (
                        <CheckboxGroup items={visibleStudyDesignFilters} selected={selectedStudyDesigns} onToggle={toggleItem(setSelectedStudyDesigns)} columns={2} />
                    );
                })()}
                {(() => {
                    const studyDesignKeys = selectedStudyDesigns.map((l) => optionKey(STUDY_DESIGN_OPTIONS, l)).filter(Boolean);
                    // Which platform's filter/edit state to show: Embase.com and Embase (Ovid)
                    // each have their own validated filter text (different syntax) and their
                    // own independent edit-override state — PubMed's box is the default for
                    // every other tab. Cochrane is a special case, handled separately below:
                    // every CENTRAL record is already a controlled trial, so Study Design
                    // isn't a concept that applies there at all — not even an AI draft.
                    const isCochraneActive = activeDatabase === "COCHRANE";
                    if (isCochraneActive) {
                        return (
                            <p className="ev-xsmall-p-dark mb-0 mt-2">
                                <i className="bi bi-info-circle me-1"></i>
                                Not applicable for Cochrane — every CENTRAL record is already a
                                controlled trial, so a Study Design filter would only remove
                                registry-only records with no abstract to search. Your selection
                                here is ignored when generating the Cochrane strategy.
                            </p>
                        );
                    }
                    const isEmbaseComActive = activeDatabase === "EMBASE" && activeEmbasePlatform === "EMBASE_EMBASECOM";
                    const isOvidActive = activeDatabase === "EMBASE" && activeEmbasePlatform === "EMBASE_OVID";
                    const defaultQuery = isEmbaseComActive
                        ? buildEmbaseComStudyDesignQuery(studyDesignKeys)
                        : isOvidActive
                        ? buildOvidStudyDesignQuery(studyDesignKeys)
                        : buildStudyDesignQuery(studyDesignKeys);
                    const isEdited = isEmbaseComActive ? embaseComStudyDesignQueryEdited : isOvidActive ? ovidStudyDesignQueryEdited : studyDesignQueryEdited;
                    const overrideValue = isEmbaseComActive ? embaseComStudyDesignQueryOverride : isOvidActive ? ovidStudyDesignQueryOverride : studyDesignQueryOverride;
                    const previewQuery = isEdited ? overrideValue : defaultQuery;
                    const setOverrideValue = isEmbaseComActive ? setEmbaseComStudyDesignQueryOverride : isOvidActive ? setOvidStudyDesignQueryOverride : setStudyDesignQueryOverride;
                    const setIsEdited = isEmbaseComActive ? setEmbaseComStudyDesignQueryEdited : isOvidActive ? setOvidStudyDesignQueryEdited : setStudyDesignQueryEdited;
                    const platformLabel = isEmbaseComActive ? "Embase.com" : isOvidActive ? "Embase (Ovid)" : "PubMed";
                    return (
                        <>
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                {(isEmbaseComActive || isOvidActive)
                                    ? <span className="ev-xsmall-p-dark mb-0">Showing {platformLabel} syntax</span>
                                    : <span></span>}
                                <button type="button"
                                    className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg"
                                    disabled={!studyDesignKeys.length}
                                    onClick={() => setShowStudyDesignQuery((v) => !v)}>
                                    <i className="bi bi-eye me-1"></i>{showStudyDesignQuery ? "Hide query" : "View query"}
                                </button>
                            </div>
                            {showStudyDesignQuery && studyDesignKeys.length > 0 && (
                                <div className="mt-2">
                                    {defaultQuery ? (
                                        <>
                                            <textarea
                                                className="formField6 ev-radius-xsmall w-100 ev-fs-11"
                                                rows={4}
                                                value={previewQuery}
                                                onChange={(e) => {
                                                    setOverrideValue(e.target.value);
                                                    setIsEdited(true);
                                                }}
                                            />
                                            <p className="ev-xsmall-p-dark mb-0 mt-1">
                                                Validated published filter for your selection — edit if needed.
                                                This exact query is injected as its own step when you build the search.
                                            </p>
                                        </>
                                    ) : (
                                        <p className="ev-xsmall-p-dark mb-0">
                                            No pre-built filter for this design on {platformLabel} —
                                            the AI will draft this filter step instead.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    );
                })()}
            </FilterGroup>

            <FilterGroup id="pubType" label="Publication Type" count={selectedPubTypes.length} open={openFilters.pubType} onToggle={toggleFilterOpen}>
                <CheckboxGroup items={PUBLICATION_TYPE_FILTERS} selected={selectedPubTypes} onToggle={toggleItem(setSelectedPubTypes)} columns={2} />
            </FilterGroup>
            {openFilters.pubType && (
                <label className="d-flex align-items-center gap-2 cursor-pointer ev-xsmall-p-dark my-2">
                    <input type="checkbox" checked={includePubTypesInSearch}
                        onChange={(e) => setIncludePubTypesInSearch(e.target.checked)} />
                    Include Publication Type in the search string
                </label>
            )}

            <FilterGroup id="demographics" label="Species, Sex & Language" count={selectedSex.length + selectedSpecies.length + selectedLanguages.length} open={openFilters.demographics} onToggle={toggleFilterOpen}>
                <FilterSection label="Species"><CheckboxGroup items={SPECIES_FILTERS} selected={selectedSpecies} onToggle={toggleItem(setSelectedSpecies)} columns={2} /></FilterSection>
                <FilterSection label="Sex"><CheckboxGroup items={SEX_FILTERS} selected={selectedSex} onToggle={toggleItem(setSelectedSex)} columns={2} /></FilterSection>
                <FilterSection label="Language"><CheckboxGroup items={LANGUAGE_FILTERS} selected={selectedLanguages} onToggle={toggleItem(setSelectedLanguages)} columns={3} /></FilterSection>
            </FilterGroup>

            <FilterGroup id="additional" label="Additional Filters" count={hasAssociatedData ? 1 : 0} open={openFilters.additional} onToggle={toggleFilterOpen}>
                <label className={`d-flex align-items-center gap-2 cursor-pointer ev-small-p-dark ev-radius-xsmall ev-padding-xsmall ${hasAssociatedData ? "" : "ev-card-3"}`}>
                    <input type="checkbox" checked={hasAssociatedData} onChange={() => setHasAssociatedData((prev) => !prev)} />
                    Has Associated Data
                </label>
            </FilterGroup>

            <FilterGroup id="geography" label="Geographic Filter" count={selectedCountries.length + selectedRegions.length + customGeoEntries.length} open={openFilters.geography} onToggle={toggleFilterOpen}>
                <FilterSection label="Countries">
                    <CheckboxGroup items={GEO_COUNTRIES} selected={selectedCountries} onToggle={toggleItem(setSelectedCountries)} columns={2} />
                </FilterSection>
                <FilterSection label="Regions">
                    <CheckboxGroup items={GEO_REGIONS} selected={selectedRegions} onToggle={toggleItem(setSelectedRegions)} columns={2} />
                </FilterSection>
                <FilterSection label="Other">
                    <div className="d-flex gap-2 align-items-center">
                        <input
                            className="formField6 ev-radius-xsmall flex-grow-1"
                            placeholder="e.g. South Korea, Scandinavia..."
                            value={customGeoInput}
                            onChange={(e) => setCustomGeoInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && customGeoInput.trim()) {
                                    const val = customGeoInput.trim();
                                    if (!customGeoEntries.includes(val)) setCustomGeoEntries((prev) => [...prev, val]);
                                    setCustomGeoInput("");
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="ev-btn-blue-outline-round ev-btn-small flex-shrink-0"
                            disabled={!customGeoInput.trim()}
                            onClick={() => {
                                const val = customGeoInput.trim();
                                if (val && !customGeoEntries.includes(val)) setCustomGeoEntries((prev) => [...prev, val]);
                                setCustomGeoInput("");
                            }}
                        >
                            <i className="bi bi-plus-lg"></i>
                        </button>
                    </div>
                    {customGeoEntries.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                            {customGeoEntries.map((entry) => (
                                <span key={entry} className="ev-badge-pill ev-badge-blue ev-fs-10 d-inline-flex align-items-center gap-1">
                                    {entry}
                                    <button
                                        type="button"
                                        className="border-0 bg-transparent p-0 lh-1"
                                        onClick={() => setCustomGeoEntries((prev) => prev.filter((e) => e !== entry))}
                                    >
                                        <i className="bi bi-x ev-fs-11"></i>
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </FilterSection>
            </FilterGroup>

            <FilterGroup id="date" label="Publication Date" count={dateFilter ? 1 : 0} open={openFilters.date} onToggle={toggleFilterOpen}>
                <CustomDropdown
                    value={dateFilter}
                    onChange={(val) => { setDateFilter(val); if (val !== "Custom range") { setCustomDateFrom(""); setCustomDateTo(""); } }}
                    options={DATE_OPTIONS}
                    placeholder="Select date range"
                />
                {dateFilter === "Custom range" && (
                    <div className="row g-1 mt-1">
                        <div className="col-6">
                            <label className="ev-small-p-dark-blue ev-bold-med mb-1 d-block">From (Year)</label>
                            <input
                                type="number"
                                className="discovery-section-header ev-radius-xsmall w-100"
                                placeholder="e.g. 2015"
                                min="1900"
                                max={new Date().getFullYear()}
                                value={customDateFrom}
                                onChange={(e) => setCustomDateFrom(e.target.value)}
                            />
                        </div>
                        <div className="col-6">
                            <label className="ev-small-p-dark-blue ev-bold-med mb-1 d-block">To (Year)</label>
                            <input
                                type="number"
                                className="discovery-section-header ev-radius-xsmall w-100"
                                placeholder="e.g. 2024"
                                min={customDateFrom || "1900"}
                                max={new Date().getFullYear()}
                                value={customDateTo}
                                onChange={(e) => setCustomDateTo(e.target.value)}
                            />
                        </div>
                    </div>
                )}
                {dateFilter && (
                    <button
                        type="button"
                        className="ev-btn-red-outline-round ev-btn-xsmall mt-2"
                        onClick={() => { setDateFilter(""); setCustomDateFrom(""); setCustomDateTo(""); }}
                    >
                        <i className="bi bi-x me-1"></i>Clear
                    </button>
                )}
            </FilterGroup>
        </>
    );

    const renderOutcomeTypes = (num) => {
        const allowedForReview = reviewTypes.length ? reviewTypes.flatMap((k) => REVIEW_TYPE_OUTCOME_MAP[k] || []) : null;
        // "Other" is neutral and always available regardless of Review Type.
        // Preserve OUTCOME_TYPES' canonical order rather than the map's order.
        const visibleOutcomeTypes = allowedForReview
            ? OUTCOME_TYPES.filter((o) => o === "Other" || allowedForReview.includes(o))
            : OUTCOME_TYPES;
        const mismatched = allowedForReview
            ? selectedOutcomeTypes.filter((o) => o !== "Other" && !allowedForReview.includes(o))
            : [];
        const isCochraneActive = activeDatabase === "COCHRANE";
        return (
            <>
                <SectionHeading number={num} title="Outcome Types" />
                <CheckboxGroup items={visibleOutcomeTypes} selected={selectedOutcomeTypes} onToggle={toggleItem(setSelectedOutcomeTypes)} />
                {isCochraneActive && (
                    <p className="ev-xsmall-p-dark mb-0 mt-2">
                        <i className="bi bi-info-circle me-1"></i>
                        Not applicable for Cochrane — CENTRAL trial registry entries describe
                        population and intervention, not results, so an outcome requirement
                        would exclude eligible trials. Your selection here is ignored when
                        generating the Cochrane strategy.
                    </p>
                )}
                {selectedOutcomeTypes.includes("Other") && (
                    <div className="mt-2">
                        <textarea
                            className="formField6 ev-radius-xsmall w-100"
                            rows={2}
                            placeholder="Describe your custom outcome type..."
                            value={otherOutcomeTypeText}
                            onChange={(e) => setOtherOutcomeTypeText(e.target.value)}
                            disabled={isProcessing}
                        />
                    </div>
                )}
                {mismatched.length > 0 && (
                    <p className="ev-xsmall-p-dark text-danger mb-0 mt-2">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        {mismatched.join(", ")} — doesn't match Review Type "
                        {reviewTypes.map((k) => REVIEW_TYPES.find((r) => r.key === k)?.label).filter(Boolean).join(" + ")}". Building or
                        saving will be blocked until this is resolved.
                    </p>
                )}
            </>
        );
    };

    const renderLeftContent = () => {
        if (inputMethod === "research_question") {
            return (
                <>
                    <textarea className="formField6 ev-radius-xsmall w-100 mb-1" rows={4}
                        placeholder="e.g. In adults with metastatic pancreatic cancer, what is the clinical effectiveness and safety of FOLFIRINOX compared with gemcitabine-based chemotherapy?"
                        value={researchQuestion} onChange={(e) => setResearchQuestion(e.target.value)} disabled={isProcessing} />
                    <p className="ev-xsmall-p-dark opacity-75 mb-2">
                        Type your question above, then let AI extract the PICOS below &mdash; you can edit anything it fills in.
                    </p>
                    <div className="mb-2">
                        <button type="button" className="ev-btn-gradient-fill-round ev-btn-reg ev-bold-reg" onClick={handleAutoFill} disabled={!researchQuestion.trim() || isAutoFilling}>
                            {isAutoFilling ? (<><i className="bi bi-hourglass-split me-1"></i> Auto-filling...</>) : (<><i className="bi bi-stars me-1"></i> Auto-fill form</>)}
                        </button>
                    </div>
                    <SectionHeading number="3" title="PICOS" badge="AI-filled · editable"
                        desc="Each field becomes its own block in the search — the more specific the term, the more precise the PubMed search." />
                    <div className="row g-3 mb-2">
                        <div className="col-6"><CharCountField label="Population" required placeholder="e.g., Metastatic pancreatic cancer" value={population} onChange={setPopulation} maxLength={500} disabled={isProcessing} /></div>
                        <div className="col-6"><CharCountField label="Intervention / Exposure" optional placeholder="e.g., Chemotherapy, smoking, radiation" value={intervention} onChange={setIntervention} maxLength={300} disabled={isProcessing} /></div>
                    </div>
                    <div className="row g-3">
                        <div className="col-6"><CharCountField label="Comparator" optional placeholder="e.g., Placebo, standard of care" value={comparator} onChange={setComparator} maxLength={300} disabled={isProcessing} /></div>
                        <div className="col-6"><CharCountField label="Outcomes" optional placeholder="e.g., Overall survival, PFS" value={outcomes} onChange={setOutcomes} maxLength={500} disabled={isProcessing} /></div>
                    </div>
                    <label className="d-flex align-items-center gap-2 cursor-pointer ev-xsmall-p-dark my-2">
                        <input type="checkbox" checked={includeInterventionInSearch}
                            onChange={(e) => setIncludeInterventionInSearch(e.target.checked)} disabled={isProcessing} />
                        Include Intervention in the search string
                    </label>
                    <label className="d-flex align-items-center gap-2 cursor-pointer ev-xsmall-p-dark my-2">
                        <input type="checkbox" checked={includeOutcomesInSearch}
                            onChange={(e) => setIncludeOutcomesInSearch(e.target.checked)} disabled={isProcessing} />
                        Include Outcomes, Other Outcomes &amp; Outcome Types in the search string
                    </label>
                    <CharCountField label="Other Outcomes" optional placeholder="e.g., Biomarkers, patient-reported outcomes" value={otherOutcomes} onChange={setOtherOutcomes} maxLength={500} rows={2} disabled={isProcessing} />
                    {renderOutcomeTypes("4")}
                    {renderFiltersPanel()}
                </>
            );
        }

        return (
            <>
                {activeDatabase === "PUBMED" ? (
                    <>
                        <SectionHeading number="3" title={<>Generate query using AI <i className="bi bi-stars gradient-font6"></i></>} />
                        <div className="mb-2">
                            <textarea className="formField6 ev-radius-xsmall w-100" rows={3}
                                placeholder="Describe your research problem and let AI draft a PubMed query for you..."
                                value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey && aiPrompt.trim() && !isGenerating) {
                                        e.preventDefault();
                                        handleGenerateFromPrompt();
                                    }
                                }}
                                disabled={isProcessing} />
                            <div className="text-end mt-1">
                                <button type="button" className="ev-btn-gradient-fill-round ev-btn-reg ev-bold-reg" onClick={handleGenerateFromPrompt} disabled={!aiPrompt.trim() || isProcessing}>
                                    {isGenerating ? (<><i className="bi bi-hourglass-split me-1"></i> Generating...</>) : (<><i className="bi bi-play-fill me-1"></i> Generate</>)}
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <SectionHeading number="3" title="Generate query using AI" />
                        <div className="ev-alert-card-warning ev-radius-xsmall mb-3">
                            <i className="bi bi-info-circle me-1"></i>
                            AI query generation is currently available for <strong>PubMed</strong> only. Switch to PubMed above to use this feature.
                        </div>
                    </>
                )}
                <div className="mb-2">
                    <label className="ev-small-p-dark-blue ev-bold-med mb-1 d-block">
                        {activeDatabase === "PUBMED"
                            ? <>Add your PubMed search query <span className="text-danger">*</span></>
                            : <>{activeDatabase.charAt(0) + activeDatabase.slice(1).toLowerCase()} search query <span className="text-danger">*</span></>}
                    </label>
                    <textarea className="formField6 ev-radius-xsmall w-100" rows={9} value={pastedQuery}
                        onChange={(e) => { setPastedQuery(e.target.value); setGeneratedQuery(e.target.value); setQueryAnalysis(null); }}
                        placeholder={
                            activeDatabase === "PUBMED"
                                ? '(("Pancreatic Neoplasms"[MeSH] OR "pancreatic cancer"[tiab] OR PDAC[tiab]) AND ...)'
                                : `Paste your ${activeDatabase.charAt(0) + activeDatabase.slice(1).toLowerCase()} search query here...`
                        }
                        disabled={isProcessing} />
                    <p className="ev-alert-card-warning ev-radius-xsmall mt-1 mb-0">
                        <i className="bi bi-info-circle me-1"></i>
                        {activeDatabase === "PUBMED"
                            ? "Paste a ready query, or generate one above. Syntax is validated live on the right."
                            : "Paste your query here. Syntax analysis is available for PubMed queries only."}
                    </p>
                </div>
            </>
        );
    };

    const renderRightPanel = () => {
        if (inputMethod === "paste_query") {
            return (
                <div className="d-flex flex-column h-100">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <p className="ev-med-p-dark-blue ev-bold-med mb-0">Query Analysis</p>
                    </div>

                    {isAnalyzing && (
                        <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-center">
                            <i className="bi bi-hourglass-split ev-fs-30 ev-reg-p-blue"></i>
                            <p className="ev-small-p-dark-blue ev-bold-med mt-2 mb-0">Analyzing your query...</p>
                        </div>
                    )}

                    {!isAnalyzing && !queryAnalysis && (
                        <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-center">
                            <i className="bi bi-code-square ev-fs-30 ev-reg-p-blue d-block mb-2"></i>
                            <p className="ev-small-p-dark-blue ev-bold-med mt-2 mb-1">No query yet</p>
                            {activeDatabase === "PUBMED" ? (
                                <>
                                    <p className="ev-xsmall-p-dark mb-3">Paste or generate a query on the left, then click Analyze.</p>
                                    <button type="button" className="ev-btn-gradient-fill-round ev-btn-reg ev-bold-med" onClick={handleAnalyzeQuery} disabled={!pastedQuery.trim()}>
                                        <i className="bi bi-stars me-1"></i> Analyze Query
                                    </button>
                                </>
                            ) : (
                                <p className="ev-xsmall-p-dark mb-3">Query syntax analysis is available for <strong>PubMed</strong> only. Switch the database below to PubMed to use this feature.</p>
                            )}
                        </div>
                    )}

                    {!isAnalyzing && queryAnalysis && (
                        <>
                            <div className={queryAnalysis.valid ? "ev-alert-card-success" : "ev-alert-card-danger"} >
                                <i className={`bi ${queryAnalysis.valid ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"} me-1`}></i>
                                {queryAnalysis.valid
                                    ? "Valid PubMed syntax — ready to run"
                                    : (queryAnalysis.validationErrors?.length
                                        ? queryAnalysis.validationErrors[0]
                                        : "Syntax issue detected — check your query")}
                            </div>

                            {queryAnalysis.validationErrors?.length > 1 && (
                                <div className="ev-alert-card-danger mt-2 ev-radius-xsmall">
                                    {queryAnalysis.validationErrors.slice(1).map((err, i) => (
                                        <p key={i} className="ev-xsmall-p-dark mb-1">• {err}</p>
                                    ))}
                                </div>
                            )}

                            {queryAnalysis.suggestedFix && (
                                <div className="ev-alert-card-warning mt-2 ev-radius-xsmall">
                                    <p className="ev-xsmall-p-dark ev-bold-med mb-1"><i className="bi bi-wrench me-1"></i>Suggested fix (smart quotes → straight quotes)</p>
                                    <button type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg mt-1"
                                        onClick={() => { setPastedQuery(queryAnalysis.suggestedFix); setGeneratedQuery(queryAnalysis.suggestedFix); }}>
                                        Apply fix
                                    </button>
                                </div>
                            )}

                            <div className="ev-card-8 ev-radius-xsmall ev-padding-small mt-3">
                                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Detected in your query</p>
                                <div className="d-flex justify-content-between border-bottom pb-1 mb-1">
                                    <span className="ev-xsmall-p-dark">MeSH terms</span>
                                    <span className="ev-small-p-dark-blue ev-bold-med">
                                        {queryAnalysis.meshCount}{queryAnalysis.meshTerms ? ` — ${queryAnalysis.meshTerms}` : ""}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom pb-1 mb-1">
                                    <span className="ev-xsmall-p-dark">Free-text (tiab)</span>
                                    <span className="ev-small-p-dark-blue ev-bold-med">
                                        {queryAnalysis.tiabCount} terms{queryAnalysis.freeTextTerms ? ` — ${queryAnalysis.freeTextTerms}` : ""}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom pb-1 mb-1">
                                    <span className="ev-xsmall-p-dark">Operators</span>
                                    <span className="ev-small-p-dark-blue ev-bold-med">
                                        {queryAnalysis.andCount} × AND · {queryAnalysis.orCount} × OR{queryAnalysis.notCount > 0 ? ` · ${queryAnalysis.notCount} × NOT` : ""}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom pb-1 mb-1">
                                    <span className="ev-xsmall-p-dark">Filters</span>
                                    <span className="ev-small-p-dark-blue ev-bold-med">
                                        {queryAnalysis.filtersDetected || "none"}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="ev-xsmall-p-dark">Est. PubMed records</span>
                                    <span className="ev-small-p-dark-blue ev-bold-med">
                                        {queryAnalysis.estimatedRecords != null
                                            ? `~${queryAnalysis.estimatedRecords.toLocaleString()}`
                                            : "—"}
                                    </span>
                                </div>
                            </div>

                            {queryAnalysis.tips?.length > 0 && (
                                <div className="ev-alert-card-warning mt-3 ev-radius-xsmall">
                                    <p className="ev-xsmall-p-dark ev-bold-med mb-1"><i className="bi bi-lightbulb me-1"></i>Tips</p>
                                    {queryAnalysis.tips.map((tip, i) => (
                                        <p key={i} className="ev-xsmall-p-dark mb-1">
                                            <i className={`bi ${tip.type === "warning" ? "bi-exclamation-triangle" : "bi-info-circle"} me-1`}></i>
                                            {tip.message}
                                        </p>
                                    ))}
                                </div>
                            )}

                            {queryAnalysis.tips?.length === 0 && !queryAnalysis.hasStudyDesign && (
                                <div className="ev-alert-card-warning mt-3 ev-radius-xsmall">
                                    <i className="bi bi-lightbulb me-1"></i>
                                    <strong>Tip:</strong> no study design filter detected — you may get case reports & editorials. Consider adding filters.
                                </div>
                            )}

                            <button type="button" className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg mt-3" onClick={handleAnalyzeQuery} disabled={isAnalyzing}>
                                <i className="bi bi-arrow-repeat me-1"></i> Re-analyze
                            </button>
                        </>
                    )}

                    <div className="mt-auto pt-3">
                        <div className="d-flex gap-2 flex-wrap mb-3">
                            {["PubMed", "Embase", "Cochrane"].map((db) => (
                                <button
                                    key={db}
                                    type="button"
                                    className={`ev-btn-small ev-radius-xlarge ${activeDatabase === db.toUpperCase() ? "ev-btn-blue-fill-round" : "ev-btn-blue-outline-round"}`}
                                    onClick={() => handleSwitchDatabase(db.toUpperCase())}
                                >
                                    {db}
                                </button>
                            ))}
                        </div>

                        <div className="d-flex gap-2 flex-wrap">
                            {renderExportButton(!pastedQuery.trim())}
                            {activeDatabase === "PUBMED" && (
                                <>
                                    <button type="button" className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg" onClick={() => fileInputRef.current?.click()}>
                                        <i className="bi bi-upload me-1"></i> Import CSV / XLSX
                                    </button>
                                    <button type="button" className="ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-bold-reg" onClick={handleDownloadSampleCsv} title="Download sample CSV to see the expected format">
                                        <i className="bi bi-file-earmark-arrow-down me-1"></i>Sample CSV
                                    </button>
                                    <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="d-none" onChange={handleImportExcel} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="d-flex flex-column h-100">
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <span className="ev-message-card4 ev-bold-med ev-fs-11">4</span>
                        <p className="ev-med-p-dark-blue ev-bold-med mb-0">Generated Search Strings</p>
                    </span>
                    <div className="d-flex gap-2 flex-wrap">
                        {isBuilt && (
                            <span style={{ animation: "ev-pop-in 0.35s ease-out" }}>
                                {renderExportButton(false)}
                            </span>
                        )}
                        {activeDatabase === "PUBMED" && (
                            <>
                                <button type="button" className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg" onClick={() => fileInputRef.current?.click()}>
                                    <i className="bi bi-upload me-1"></i> Import CSV / XLSX
                                </button>
                                <button type="button" className="ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-bold-reg" onClick={handleDownloadSampleCsv} title="Download sample CSV to see the expected format">
                                    <i className="bi bi-file-earmark-arrow-down me-1"></i>Sample CSV
                                </button>
                                <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="d-none" onChange={handleImportExcel} />
                            </>
                        )}
                    </div>
                </div>

                {!isBuilt ? (
                    <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-center">
                        <i className="bi bi-hash ev-fs-30"
                            style={population.trim()
                                ? { color: "var(--color2)", display: "inline-block", animation: "ev-breathe 1.6s ease-in-out infinite" }
                                : { color: "var(--color14)" }}
                        ></i>
                        <p className="ev-small-p-dark-blue ev-bold-med mt-2 mb-1">Strings build from your PICOS</p>
                        <p className="ev-xsmall-p-dark mb-3">Fill in the fields on the left, then click Build to generate steps here.</p>
                        <button type="button" className="ev-btn-gradient-fill-round ev-btn-reg ev-bold-med"
                            style={justAutoFilled ? { animation: "ev-cta-pulse 1.1s ease-in-out 2" } : undefined}
                            onClick={handleBuildStrings} disabled={!population.trim() || isProcessing}>
                            {isGenerating ? (<><i className="bi bi-hourglass-split me-1"></i> Building...</>) : (<><i className="bi bi-stars me-1"></i> Build search strings →</>)}
                        </button>
                    </div>
                ) : activeSteps.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-center">
                        <i className="bi bi-hash ev-fs-30" style={{ color: "var(--color14)" }}></i>
                        <p className="ev-small-p-dark-blue ev-bold-med mt-2 mb-1">No steps generated</p>
                        <p className="ev-xsmall-p-dark mb-3">Fill in PICOS fields on the left first, then build.</p>
                        <button type="button" className="ev-btn-blue-fill-round ev-btn-reg ev-bold-med" onClick={handleBuildStrings} disabled={!population.trim() || isProcessing}>
                            {isGenerating ? (<><i className="bi bi-hourglass-split me-1"></i> Building...</>) : (<><i className="bi bi-stars me-1"></i> Build search strings →</>)}
                        </button>
                    </div>
                ) : (
                    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                        {activeDatabase === "PUBMED" ? (
                            <>
                                {queryOutOfDate && (
                                    <div className="ev-alert-card-warning mb-3">
                                        <i className="bi bi-arrow-repeat me-1"></i>
                                        {changedConcepts.length > 0
                                            ? <>Only <strong>{changedConcepts.join(", ")}</strong> step{changedConcepts.length > 1 ? "s" : ""} will be updated — other steps stay as-is.</>
                                            : <>Filters or date changed — rebuild to update search strings.</>}
                                    </div>
                                )}
                                            
                                <p className="ev-xsmall-p-dark opacity-75 mb-1">Preview &amp; edit the string for:</p>
                                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                    <div className="d-flex gap-2 flex-wrap align-items-center">
                                        {["PubMed", "Embase", "Cochrane"].map((db) => {
                                            const key = db.toUpperCase();
                                            const isLive = key === "PUBMED";
                                            return (
                                                <button
                                                    key={db}
                                                    type="button"
                                                    className={`ev-btn-small ev-radius-xlarge d-inline-flex align-items-center gap-1 ${activeDatabase === key ? "ev-btn-blue-fill-round" : "ev-btn-blue-outline-round"}`}
                                                    onClick={() => { handleSwitchDatabase(key); setQueryAnalysis(null); }}
                                                >
                                                    {db}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <button type="button" className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg flex-shrink-0" onClick={handleAddCustomStep}>
                                        <i className="bi bi-plus-lg me-1"></i> Add step manually
                                    </button>
                                </div>
                                <div className="flex-grow-1 scrollable-element pe-1">
                                    {activeSteps.filter(s => !s.isFinal).map((step, idx) => (
                                        <div key={step.id}>
                                            <StepCard
                                                index={idx}
                                                description={
                                                    /^Step\s*\d+$/i.test(step.description || "")
                                                        ? `Step ${idx + 1}`
                                                        : step.description
                                                }
                                                query={step.query}
                                                isCustom={!step.auto}
                                                isFinal={false}
                                                editing={editingStepId === step.id}
                                                onEdit={() => setEditingStepId(editingStepId === step.id ? null : step.id)}
                                                onDelete={() => handleRemoveStep(step.id)}
                                                onFieldChange={(field, value) => handleStepFieldChange(step.id, field, value)}
                                                onSaveEdit={() => {
                                                    handleSaveOtherDbStepEdit(step.id, step.description, step.query);
                                                    setEditingStepId(null);
                                                }}
                                            />
                                            {editingStepId === step.id && (
                                                <label className="d-flex align-items-center gap-2 ev-xsmall-p-dark mb-2 ms-1" style={{ marginTop: -6 }}>
                                                    <input type="checkbox" checked={applyEditToOtherDbs}
                                                        onChange={(e) => setApplyEditToOtherDbs(e.target.checked)} />
                                                    Also apply this change to other databases with this step
                                                </label>
                                            )}
                                        </div>
                                    ))}
                                    {(() => {
                                        const finalStep = activeSteps.find(s => s.isFinal);
                                        return (
                                            <StepCard
                                                index={activeSteps.filter(s => !s.isFinal).length}
                                                description={finalStep?.description || "Combine all"}
                                                query={derivedFinalQuery}
                                                isFinal
                                                editing={editingStepId === finalStep?.id}
                                                onEdit={finalStep ? () => setEditingStepId(editingStepId === finalStep.id ? null : finalStep.id) : undefined}
                                                onFieldChange={finalStep ? (field, value) => handleStepFieldChange(finalStep.id, field, value) : undefined}
                                                onSaveEdit={() => setEditingStepId(null)}
                                            />
                                        );
                                    })()}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Not a database switch — these are read-only reference strings for
                                    the same search. */}
                                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                    <div className="d-flex gap-2 flex-wrap align-items-center">
                                        {["PubMed", "Embase", "Cochrane"].map((db) => {
                                            const key = db.toUpperCase();
                                            const isLive = key === "PUBMED";
                                            return (
                                                <button
                                                    key={db}
                                                    type="button"
                                                    className={`ev-btn-small ev-radius-xlarge d-inline-flex align-items-center gap-1 ${activeDatabase === key ? "ev-btn-blue-fill-round" : "ev-btn-blue-outline-round"}`}
                                                    onClick={() => handleSwitchDatabase(key)}
                                                    disabled={isRegenerating}
                                                >
                                                    {db}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {activeDatabase === "EMBASE" && (
                                        <div className="d-flex gap-2 flex-shrink-0">
                                            {EMBASE_PLATFORMS.map((p) => (
                                                <button
                                                    key={p.key}
                                                    type="button"
                                                    className={`ev-btn-xsmall ev-radius-xlarge ${activeEmbasePlatform === p.key ? "ev-btn-blue-fill-round" : "ev-btn-blue-outline-round"}`}
                                                    onClick={() => setActiveEmbasePlatform(p.key)}
                                                    disabled={isRegenerating}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex-grow-1 scrollable-element pe-1">
                                {dbSteps[activePlatformKey]?.steps?.length > 0 ? (
                                    <>
                                        {(() => {
                                            const nonFinalOther = activeOtherDbSteps.filter((s) => !s.is_final);
                                            const remappedFinalQuery = getDisplayFinalQuery(activePlatformKey);
                                            return activeOtherDbSteps.map((step) => {
                                                const isEditingThis = otherDbEditingStep === step.step_number;
                                                const displayIdx = step.is_final
                                                    ? nonFinalOther.length
                                                    : nonFinalOther.findIndex((s) => s.step_number === step.step_number);
                                                return (
                                                    <div key={step.step_number}>
                                                        <StepCard
                                                            index={displayIdx}
                                                            description={
                                                                /^Step\s*\d+$/i.test(step.description || "")
                                                                    ? `Step ${displayIdx + 1}`
                                                                    : (step.description || `Step ${displayIdx + 1}`)
                                                            }
                                                            query={step.is_final ? remappedFinalQuery : step.query_string}
                                                            isFinal={step.is_final}
                                                            editing={isEditingThis}
                                                            onEdit={() => {
                                                                setOtherDbEditingStep(isEditingThis ? null : step.step_number);
                                                            }}
                                                            onFieldChange={(field, value) => {
                                                                const key = field === "query" ? "query_string" : field;
                                                                setOtherDbStepEdits((prev) => ({
                                                                    ...prev,
                                                                    [activePlatformKey]: {
                                                                        ...(prev[activePlatformKey] || {}),
                                                                        [step.step_number]: {
                                                                            ...(prev[activePlatformKey]?.[step.step_number] || {
                                                                                description: step.description, query_string: step.query_string,
                                                                            }),
                                                                            [key]: value,
                                                                        },
                                                                    },
                                                                }));
                                                            }}
                                                            onSaveEdit={() => handleSaveOtherDbStepEdit(
                                                                step.step_number, step.description, step.query_string
                                                            )}
                                                            onDelete={() => {
                                                                if (step.is_final) return;
                                                                setOtherDbRemovedSteps((prev) => {
                                                                    const next = { ...prev };
                                                                    next[activePlatformKey] = [...new Set([...(next[activePlatformKey] || []), step.step_number])];
                                                                    Object.keys(dbSteps).filter((db) => db !== "PUBMED" && db !== activePlatformKey).forEach((db) => {
                                                                        const targetStepNumber = findStepNumberByOriginKey(db, step.origin_key);
                                                                        if (targetStepNumber != null) {
                                                                            next[db] = [...new Set([...(next[db] || []), targetStepNumber])];
                                                                        }
                                                                    });
                                                                    return next;
                                                                });
                                                                if (otherDbEditingStep === step.step_number) {
                                                                    setOtherDbEditingStep(null);
                                                                }
                                                            }}
                                                        />
                                                        {isEditingThis && !step.is_final && (
                                                            <label className="d-flex align-items-center gap-2 ev-xsmall-p-dark mb-2 ms-1" style={{ marginTop: -6 }}>
                                                                <input type="checkbox" checked={applyEditToOtherDbs}
                                                                    onChange={(e) => setApplyEditToOtherDbs(e.target.checked)} />
                                                                Also apply this change to other databases with this step
                                                            </label>
                                                        )}
                                                    </div>
                                                );
                                            });
                                        })()}
                                        {/* {dbSteps[activeDatabase]?.unsupported?.length > 0 && (
                                            <div className="ev-alert-card-warning ev-radius-xsmall mt-1">
                                                <i className="bi bi-exclamation-triangle me-1"></i>
                                                <strong>Unsupported terms</strong> kept as-is: {dbSteps[activeDatabase].unsupported.join(", ")}
                                            </div>
                                        )} */}
                                    </>
                                ) : (
                                    <p className="ev-xsmall-p-dark mb-0">
                                        {isBuilt
                                            ? `No steps generated for ${DB_LABELS[activePlatformKey] || activePlatformKey} yet — try rebuilding.`
                                            : "Build search strings first — all databases generate together."}
                                    </p>
                                )}
                                </div>
                            </>
                        )}

                        {/* Rebuild — centered, gradient, now shown on every DB tab */}
                        {(() => {
                            // All four platforms (PubMed, Cochrane, Embase.com, Embase Ovid)
                            // have a real backend generator now.
                            const isActiveTabSupported = true;
                            return (
                                <div className="d-flex justify-content-center mt-3">
                                    <button type="button"
                                        className="ev-btn-reg ev-btn-gradient-fill-round ev-bold-med ev-radius-xlarge px-4"
                                        onClick={handleRebuildActiveTab}
                                        disabled={isProcessing || isRegenerating || isRestoringSavedSteps || !isActiveTabSupported}
                                        title={isRestoringSavedSteps
                                            ? "Loading your saved search strategies…"
                                            : !isActiveTabSupported
                                            ? `${DB_LABELS[activePlatformKey] || activePlatformKey} generation isn't available yet`
                                            : undefined}>
                                        {(isGenerating || isRegenerating)
                                            ? (<><i className="bi bi-hourglass-split me-1"></i> Rebuilding...</>)
                                            : (<><i className="bi bi-stars me-1"></i> Rebuild search strings →</>)}
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <style>{`
                @keyframes ev-cta-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.45); }
                    50% { box-shadow: 0 0 0 8px rgba(124, 58, 237, 0); }
                }
                @keyframes ev-pop-in {
                    0% { opacity: 0; transform: scale(0.85) translateY(-2px); }
                    100% { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes ev-breathe {
                    0%, 100% { opacity: 0.55; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.08); }
                }
            `}</style>
            <div className="d-flex align-items-center justify-content-between mx-2 mb-1">
                <p className="ev-med-p-dark-blue ev-bold-med mb-0">
                    {isEditMode ? "Edit Search" : "Create Search"}
                </p>
                <div className="d-flex align-items-center gap-2">
                    <button type="button" className="ev-btn-blue-outline-round ev-btn-reg ev-bold-reg" onClick={onClose} disabled={isProcessing}>Cancel</button>
                    <span title={(!canSave && activeDatabase !== "PUBMED" && inputMethod !== "paste_query") ? "Search can only be saved when PubMed is selected" : undefined}>
                        <button type="button" className="ev-btn-blue-fill-round ev-btn-reg ev-bold-reg" disabled={!canSave || isProcessing} onClick={handleCreateSearch}>
                            {isCreating
                                ? (isEditMode ? "Saving..." : "Creating...")
                                : (isEditMode ? "Save changes" : "Save & open dashboard")}
                        </button>
                    </span>
                </div>
            </div>

            {!isEditMode && (
                <p className="ev-xsmall-p-dark mx-2 mb-2">
                    Describe your question or paste a PICOS &mdash; the AI fills in Population, Intervention, Comparator and Outcome, then builds a ready-to-run PubMed search string from them.
                </p>
            )}

            <ResizablePanels
                leftPanel={
                    <div className="ev-card-4 ev-padding-small ev-radius-reg h-100 d-flex flex-column">
                        <div className="mb-3">
                            <label className="ev-reg-p-dark-blue ev-bold-med mb-1 d-block">
                                Name <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="formField6 ev-radius-xsmall w-100"
                                placeholder="e.g. Semaglutide CV outcomes"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                disabled={isProcessing}
                            />
                        </div>
                        {/* Everything below Name scrolls as one. The Review Type picker
                            used to sit outside the scroll area, so on a short viewport it
                            stayed pinned while the form beneath it got its own cramped
                            scrollbar. minHeight: 0 is required — without it this flex
                            child refuses to shrink and no scrollbar ever appears. The
                            inline overflowY is required too: the parent's overflow
                            rule wins over .scrollable-element's otherwise. */}
                        <div className="scrollable-element flex-grow-1 pe-3"
                             style={{ overflowY: "auto", minHeight: 0 }}>
                        <div className={`mb-3 pb-2 border-bottom mt-0`}>
                            <button type="button"
                                className="d-flex align-items-center justify-content-between w-100 bg-transparent border-0 p-0 shadow-none"
                                style={{ boxShadow: "none" }}
                                onClick={() => toggleFilterOpen("reviewType")}>
                                <span className="d-flex align-items-center gap-2">
                                    <span className="ev-message-card4 ev-bold-med ev-fs-11">1</span>
                                    <span className="ev-reg-p-dark-blue ev-bold-med">Review Type</span>
                                </span>
                                <i className={`bi ${openFilters.reviewType ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11`}></i>
                            </button>
                            {openFilters.reviewType && (
                                <p className="ev-xsmall-p-dark mb-0 mt-1 ms-4 ps-1">
                                    Pick one or a validated pair before filling the rest of the form — it
                                    auto-selects the matching Outcome Types below, and is sent to the AI
                                    along with your research question.
                                </p>
                            )}
                        </div>
                        {openFilters.reviewType && (
                        <div className="mb-3">
                            <div className="position-relative">
                                {/* Fade masks sit above the scroll content but below the arrow
                                    buttons, so a button scrolling under an arrow fades out
                                    smoothly instead of being hard-clipped by padding. */}
                                <div style={{
                                    position: "absolute", left: 0, top: 0, bottom: "4px", width: "36px",
                                    background: "linear-gradient(to right, #fff 40%, rgba(255,255,255,0))",
                                    zIndex: 1, pointerEvents: "none",
                                }}></div>
                                <div style={{
                                    position: "absolute", right: 0, top: 0, bottom: "4px", width: "36px",
                                    background: "linear-gradient(to left, #fff 40%, rgba(255,255,255,0))",
                                    zIndex: 1, pointerEvents: "none",
                                }}></div>
                                <button type="button"
                                    onClick={() => reviewTypeScrollRef.current?.scrollBy({ left: -180, behavior: "smooth" })}
                                    aria-label="Scroll left"
                                    style={{
                                        position: "absolute", left: "-4px", top: "50%",
                                        transform: "translateY(-50%)", zIndex: 2,
                                        width: "26px", height: "26px", borderRadius: "50%",
                                        border: "1px solid #d8dce6", background: "#fff",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.12)", padding: 0,
                                    }}>
                                    <i className="bi bi-chevron-left" style={{ fontSize: "12px", color: "#4a5568" }}></i>
                                </button>
                                <div
                                    ref={reviewTypeScrollRef}
                                    className="review-type-scroll d-flex gap-2 mb-1"
                                    style={{
                                        overflowX: "auto", overflowY: "hidden", flexWrap: "nowrap",
                                        scrollbarWidth: "none", msOverflowStyle: "none",
                                        padding: "0 34px",
                                    }}
                                >
                                    {REVIEW_TYPES.map((rt) => {
                                        const isSelected = reviewTypes.includes(rt.key);
                                        const isDisallowed = !isSelected && reviewTypes.length > 0 && (
                                            reviewTypes.length >= 2 ||
                                            !reviewTypes.every((existing) => (REVIEW_TYPE_COMPATIBILITY[existing] || []).includes(rt.key))
                                        );
                                        return (
                                            <button key={rt.key} type="button"
                                                className={`ev-radius-xsmall ev-small-p-dark p-2 px-3 shadow-none flex-shrink-0 ${isSelected ? "ev-card-6 ev-bold-med" : "ev-card-8"}`}
                                                style={{ boxShadow: "none", opacity: isDisallowed ? 0.5 : 1, whiteSpace: "nowrap" }}
                                                title={isDisallowed ? "Not a supported combination with your current selection" : undefined}
                                                onClick={() => handleToggleReviewType(rt.key)}
                                                disabled={isProcessing}>
                                                {rt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button type="button"
                                    onClick={() => reviewTypeScrollRef.current?.scrollBy({ left: 180, behavior: "smooth" })}
                                    aria-label="Scroll right"
                                    style={{
                                        position: "absolute", right: "-4px", top: "50%",
                                        transform: "translateY(-50%)", zIndex: 2,
                                        width: "26px", height: "26px", borderRadius: "50%",
                                        border: "1px solid #d8dce6", background: "#fff",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.12)", padding: 0,
                                    }}>
                                    <i className="bi bi-chevron-right" style={{ fontSize: "12px", color: "#4a5568" }}></i>
                                </button>
                            </div>
                            <style>{`.review-type-scroll::-webkit-scrollbar { display: none; }`}</style>
                            {!reviewTypes.length && (
                                <p className="ev-xsmall-p-dark text-danger mb-0 mt-1">
                                    Required — select a Review Type to continue.
                                </p>
                            )}
                            {reviewTypeWarning && (
                                <p className="ev-xsmall-p-dark text-danger mb-0 mt-1">
                                    <i className="bi bi-exclamation-triangle me-1"></i>{reviewTypeWarning}
                                </p>
                            )}
                        </div>
                        )}
                        {!reviewTypes.length && (
                            <p className="ev-xsmall-p-dark text-danger mb-2">
                                <i className="bi bi-lock-fill me-1"></i>
                                Select a Review Type above to unlock the rest of the form.
                            </p>
                        )}
                        <fieldset
                            disabled={!reviewTypes.length || isProcessing}
                            className="p-0 m-0 border-0"
                            style={{ minWidth: 0 }}
                        >
                            <SectionHeading number="2" title="Input Method" compact
                                desc="Research Query: describe your question in plain English. Custom Query: already have a search string — paste and refine it." />
                            <div className="d-flex gap-2 mb-3">
                                {INPUT_METHODS.map((method) => (
                                    <button key={method.id} type="button"
                                        className={`flex-fill text-center ev-radius-xsmall ev-small-p-dark p-2 ${inputMethod === method.id ? "ev-card-6 ev-bold-med" : "ev-card-8"}`}
                                        onClick={() => setInputMethod(method.id)}>
                                        <i className={`bi ${method.icon} d-block mb-1 ev-fs-16${method.icon === "bi-stars" ? " gradient-font6" : ""}`}></i>
                                        <span className="ev-bold-reg">{method.label}</span>
                                    </button>
                                ))}
                            </div>
                            <div>
                                {renderLeftContent()}
                            </div>
                        </fieldset>
                        </div>
                        <div className="mt-3 pt-3 border-top">
                            <div className="d-flex justify-content-end gap-2">
                                <button type="button" className="ev-btn-lightblue-fill-round ev-btn-reg ev-bold-reg" onClick={clearForm} disabled={isProcessing}>
                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Reset
                                </button>
                            </div>
                        </div>
                    </div>
                }
                rightPanel={
                    <div className="ev-card-4 ev-padding-small ev-radius-reg h-100 d-flex flex-column" style={{ overflow: "hidden" }}>
                        {renderRightPanel()}
                    </div>
                }
            />

            {isProcessingPopupVisible && ReactDOM.createPortal(<ProcessingPopup message={processingMessage || "Processing..."} />, document.body)}
            {popupErrorMessage && ReactDOM.createPortal(<ErrorPopup message={popupErrorMessage} onClose={() => setPopupErrorMessage("")} />, document.body)}
            {popupSuccessMessage && ReactDOM.createPortal(<SuccessPopup message={popupSuccessMessage} onClose={() => setPopupSuccessMessage("")} />, document.body)}

            {/* ── CSV import preview modal — validated steps awaiting confirmation ── */}
            {pendingImport !== null && ReactDOM.createPortal(
                <div
                    className="slr-pdf-modal-overlay"
                    onClick={(e) => { if (e.target === e.currentTarget) handleCancelImport(); }}
                >
                    <div className="ev-card-4 ev-radius-reg ev-padding-small d-flex flex-column" style={{ width: "min(94vw, 640px)", maxHeight: "80vh" }}>
                        {/* Header */}
                        <div className="d-flex align-items-start justify-content-between pb-3 mb-3 border-bottom flex-shrink-0">
                            <div>
                                <p className="ev-med-p-dark-blue ev-bold-med mb-0">
                                    <i className="bi bi-check-circle-fill ev-reg-p-blue me-1"></i>Mapping Verified
                                </p>
                                <p className="ev-xsmall-p-dark mb-0">{pendingImport.length} valid PubMed step(s) ready to import</p>
                            </div>
                            <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall ms-3 flex-shrink-0" onClick={handleCancelImport}>
                                <i className="bi bi-x ev-fs-16"></i>
                            </button>
                        </div>

                        {/* Scrollable row list */}
                        <div className="flex-grow-1 scrollable-element pe-1 mb-3" style={{ overflowY: "auto" }}>
                            {pendingImport.map((row, idx) => (
                                <div key={row.id} className="ev-card-3 ev-radius-xsmall ev-padding-xsmall mb-2">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span className="ev-message-card4 ev-bold-med ev-fs-11">{idx + 1}</span>
                                        <span className="ev-small-p-dark ev-bold-reg">{row.description}</span>
                                        <i className="bi bi-check-circle-fill ev-reg-p-blue ms-auto ev-fs-11"></i>
                                    </div>
                                    <code className="ev-fs-11 d-block" style={{ color: "var(--color5)", wordBreak: "break-word" }}>{row.query}</code>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="d-flex gap-2 pt-3 border-top flex-shrink-0">
                            <button type="button" className="ev-btn-blue-outline-round ev-btn-reg ev-bold-reg" onClick={handleCancelImport}>
                                Cancel
                            </button>
                            <button type="button" className="ev-btn-blue-fill-round ev-btn-reg ev-bold-reg flex-grow-1" onClick={handleConfirmImport}>
                                <i className="bi bi-download me-1"></i>Confirm & Import
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default CreateLiteratureSearchForm;